<template>
  <div>
  <!-- Login / register / org selector render standalone, no shell -->
  <router-view v-if="isStandaloneRoute" />
  <div v-else
       class="app"
       :class="{ collapsed: sidebarCollapsed, 'mobile-open': mobileNavOpen }">
    <!-- Backdrop: shown only when the sidebar is open as a drawer on mobile -->
    <div class="sidebar-backdrop" v-if="mobileNavOpen" @click="closeMobileNav"></div>

    <!-- ── SIDEBAR ─────────────────────────────────────────── -->
    <aside class="sidebar" :aria-hidden="String(!mobileNavOpen && isMobileViewport)">
      <div class="sidebar-header">
        <div class="logo" style="cursor:pointer" @click="goHome">
          <!-- Collapsed: small MeasureUp icon -->
          <template v-if="sidebarCollapsed">
            <img v-if="!iconFailed"
                 src="https://www.measureup.com/favicon.ico"
                 alt="MU"
                 class="logo-icon"
                 @error="iconFailed=true" />
            <div v-else class="logo-mark"></div>
          </template>
          <!-- Expanded: real logo image or text fallback -->
          <template v-else>
            <img v-if="!logoFailed"
                 :src="brandLogoUrl"
                 :alt="brandName"
                 class="logo-img"
                 @error="logoFailed=true" />
            <template v-else>
              <div class="logo-mark"></div>
              <span class="logo-text">measure<span style="color:var(--accent)">Up</span></span>
            </template>
          </template>
        </div>
        <!-- Close button: only rendered as an interaction target on phones. -->
        <button class="sidebar-close" :aria-label="$t('ui.nav.closeMenu')" @click="closeMobileNav">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>

      <div class="sidebar-scroll">
      <!-- Favorites section (shown when any favorites exist) -->
      <template v-if="favorites.length > 0">
        <div class="sidebar-section-label">{{ $t('ui.nav.favorites') }}</div>
        <div class="nav-group">
          <div v-for="fav in favoritedItems" :key="'fav-'+fav.to"
               class="nav-subitem fav-item"
               :class="{active: $route.path === fav.to}"
               @click="$router.push(fav.to)">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="var(--warning)" stroke="none" style="flex-shrink:0"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
            {{ fav.label }}
          </div>
        </div>
        <div style="padding:0 16px 6px"><div style="height:1px;background:rgba(255,255,255,0.08)"></div></div>
      </template>

      <div class="sidebar-section-label">{{ $t('ui.nav.products') }}</div>
      <div class="nav-group" id="productNav">
        <div v-for="section in orderedProductSections" :key="section.key" class="nav-section" @mouseenter="onSectionHover" @mouseleave="onSectionLeave">
          <div class="nav-item expandable"
               :class="{active: activeProduct===section.key, expanded: openSection===section.key}"
               @click="toggleSection(section.key)">
            <svg class="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" v-html="section.svg"></svg>
            <span class="nav-label">{{ $t(section.i18nKey) }}</span>
            <svg class="nav-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="9 18 15 12 9 6"/></svg>
          </div>
          <div class="nav-subitems" :class="{open: openSection===section.key}" :style="flyoutStyle">
            <div class="nav-flyout-title">{{ $t(section.i18nKey) }}</div>
            <!-- Cascade (multi-level hover) layout: Partner Portal only. -->
            <template v-if="section.cascade">
            <template v-for="(group, gi) in navGroupsFor(section.itemsKey)">
              <!-- Single-leaf group: the group row itself is a direct link. -->
              <div v-if="group.items.length === 1" :key="'g'+gi+section.key" class="nav-subitem-wrap nav-flyout-group is-leaf">
                <router-link class="nav-subitem" active-class="active" :exact="group.items[0].exact" :to="group.items[0].to">
                  {{ group.heading ? $t(group.heading) : $t(group.items[0].i18nKey) }}
                </router-link>
                <button v-if="section.hasFavorites" class="fav-toggle" :class="{active: favorites.includes(group.items[0].to)}"
                        :title="favorites.includes(group.items[0].to) ? 'Remove from favorites' : 'Add to favorites'"
                        @click.prevent.stop="toggleFavorite(group.items[0].to)">
                  <svg width="11" height="11" viewBox="0 0 24 24"
                       :fill="favorites.includes(group.items[0].to) ? 'var(--warning)' : 'none'"
                       :stroke="favorites.includes(group.items[0].to) ? 'var(--warning)' : 'currentColor'"
                       stroke-width="2">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                </button>
              </div>
              <!-- Multi-leaf group: hoverable row that cascades a level-2 subpanel. -->
              <div v-else :key="'g'+gi+section.key" class="nav-flyout-group has-children"
                   :class="{'is-open': openGroupId === section.key+'-'+gi}"
                   :data-group-id="section.key+'-'+gi"
                   @mouseenter="onGroupHover" @mouseleave="onGroupLeave">
                <div class="nav-flyout-group-row">
                  <span class="nav-flyout-group-title">{{ $t(group.heading) }}</span>
                  <svg class="nav-flyout-group-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="9 18 15 12 9 6"/></svg>
                </div>
                <div class="nav-subpanel">
                  <div v-for="item in group.items" :key="item.to+section.key" class="nav-subitem-wrap">
                    <router-link class="nav-subitem" active-class="active" :exact="item.exact" :to="item.to">{{ $t(item.i18nKey) }}</router-link>
                    <button v-if="section.hasFavorites" class="fav-toggle" :class="{active: favorites.includes(item.to)}"
                            :title="favorites.includes(item.to) ? 'Remove from favorites' : 'Add to favorites'"
                            @click.prevent.stop="toggleFavorite(item.to)">
                      <svg width="11" height="11" viewBox="0 0 24 24"
                           :fill="favorites.includes(item.to) ? 'var(--warning)' : 'none'"
                           :stroke="favorites.includes(item.to) ? 'var(--warning)' : 'currentColor'"
                           stroke-width="2">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </template>
            </template>
            <!-- Flat (single-level) layout: Learn, Authoring, iTalentUp. -->
            <template v-else v-for="(group, gi) in navGroupsFor(section.itemsKey)">
              <div v-if="group.heading" :key="'g'+gi+section.key" class="nav-flyout-group-label">{{ $t(group.heading) }}</div>
              <div v-for="item in group.items" :key="item.to+section.key" class="nav-subitem-wrap">
                <router-link class="nav-subitem" active-class="active" :exact="item.exact" :to="item.to">{{ $t(item.i18nKey) }}</router-link>
                <button v-if="section.hasFavorites" class="fav-toggle" :class="{active: favorites.includes(item.to)}"
                        :title="favorites.includes(item.to) ? 'Remove from favorites' : 'Add to favorites'"
                        @click.prevent.stop="toggleFavorite(item.to)">
                  <svg width="11" height="11" viewBox="0 0 24 24"
                       :fill="favorites.includes(item.to) ? 'var(--warning)' : 'none'"
                       :stroke="favorites.includes(item.to) ? 'var(--warning)' : 'currentColor'"
                       stroke-width="2">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                </button>
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- Enterprise section -->
      <template v-if="isEnterprise">
        <div style="padding:12px 16px 6px"><div style="height:1px;background:rgba(255,255,255,0.08)"></div></div>
        <div class="sidebar-section-label">{{ $t('ui.nav.enterprise') }}</div>
        <div class="nav-group">
          <div v-for="section in orderedEnterpriseSections" :key="section.key" class="nav-section" @mouseenter="onSectionHover" @mouseleave="onSectionLeave">
            <div class="nav-item secondary expandable"
                 :class="{active: isSectionActive(section), expanded: openSection===section.key}"
                 @click="toggleSection(section.key)">
            <svg class="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" v-html="section.svg"></svg>
              <span class="nav-label">{{ $t(section.i18nKey) }}</span>
              <svg class="nav-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="9 18 15 12 9 6"/></svg>
            </div>
            <div class="nav-subitems" :class="{open: openSection===section.key}" :style="flyoutStyle">
              <div class="nav-flyout-title">{{ $t(section.i18nKey) }}</div>
              <!-- Cascade (multi-level hover) layout: opt-in per section. -->
              <template v-if="section.cascade">
              <template v-for="(group, gi) in navGroupsFor(section.itemsKey)">
                <div v-if="group.items.length === 1" :key="'g'+gi+section.key" class="nav-subitem-wrap nav-flyout-group is-leaf">
                  <router-link class="nav-subitem" active-class="active" :exact="group.items[0].exact" :to="group.items[0].to">
                    {{ group.heading ? $t(group.heading) : $t(group.items[0].i18nKey) }}
                  </router-link>
                  <button v-if="section.hasFavorites" class="fav-toggle" :class="{active: favorites.includes(group.items[0].to)}"
                          @click.prevent.stop="toggleFavorite(group.items[0].to)">
                    <svg width="11" height="11" viewBox="0 0 24 24"
                         :fill="favorites.includes(group.items[0].to) ? 'var(--warning)' : 'none'"
                         :stroke="favorites.includes(group.items[0].to) ? 'var(--warning)' : 'currentColor'"
                         stroke-width="2">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                    </svg>
                  </button>
                </div>
                <div v-else :key="'g'+gi+section.key" class="nav-flyout-group has-children"
                     :class="{'is-open': openGroupId === section.key+'-'+gi}"
                     :data-group-id="section.key+'-'+gi"
                     @mouseenter="onGroupHover" @mouseleave="onGroupLeave">
                  <div class="nav-flyout-group-row">
                    <span class="nav-flyout-group-title">{{ $t(group.heading) }}</span>
                    <svg class="nav-flyout-group-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="9 18 15 12 9 6"/></svg>
                  </div>
                  <div class="nav-subpanel">
                    <div v-for="item in group.items" :key="item.to+section.key" class="nav-subitem-wrap">
                      <router-link class="nav-subitem" active-class="active" :to="item.to">{{ $t(item.i18nKey) }}</router-link>
                      <button v-if="section.hasFavorites" class="fav-toggle" :class="{active: favorites.includes(item.to)}"
                              @click.prevent.stop="toggleFavorite(item.to)">
                        <svg width="11" height="11" viewBox="0 0 24 24"
                             :fill="favorites.includes(item.to) ? 'var(--warning)' : 'none'"
                             :stroke="favorites.includes(item.to) ? 'var(--warning)' : 'currentColor'"
                             stroke-width="2">
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </template>
              </template>
              <!-- Flat (single-level) layout. -->
              <template v-else v-for="(group, gi) in navGroupsFor(section.itemsKey)">
                <div v-if="group.heading" :key="'g'+gi+section.key" class="nav-flyout-group-label">{{ $t(group.heading) }}</div>
                <div v-for="item in group.items" :key="item.to+section.key" class="nav-subitem-wrap">
                  <router-link class="nav-subitem" active-class="active" :to="item.to">{{ $t(item.i18nKey) }}</router-link>
                  <button v-if="section.hasFavorites" class="fav-toggle" :class="{active: favorites.includes(item.to)}"
                          @click.prevent.stop="toggleFavorite(item.to)">
                    <svg width="11" height="11" viewBox="0 0 24 24"
                         :fill="favorites.includes(item.to) ? 'var(--warning)' : 'none'"
                         :stroke="favorites.includes(item.to) ? 'var(--warning)' : 'currentColor'"
                         stroke-width="2">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                    </svg>
                  </button>
                </div>
              </template>
            </div>
          </div>
        </div>
      </template>
      </div>

      <div class="sidebar-footer">
        <button v-if="isEnterprise" class="sidebar-footer-btn" :title="$t('ui.nav.reorderSections')" @click="openReorder">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="3" y1="6" x2="11" y2="6"/><line x1="3" y1="12" x2="14" y2="12"/><line x1="3" y1="18" x2="8" y2="18"/><polyline points="17 9 21 5 17 1"/><polyline points="17 23 21 19 17 15"/><line x1="21" y1="5" x2="21" y2="19"/></svg>
          <span class="sidebar-footer-btn-label">{{ $t('ui.nav.reorderSections') }}</span>
        </button>
        <div class="collapse-btn" @click="toggleSidebar">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          <span class="collapse-btn-label">{{ $t('ui.nav.collapse') }}</span>
        </div>
      </div>
    </aside>

    <!-- ── MAIN AREA ──────────────────────────────────────── -->
    <div class="main-area">
      <div v-if="isImpersonating" class="impersonation-banner">
        <span>
          You are impersonating <b>{{ (user && (user.first_name + ' ' + user.last_name)) || 'a user' }}</b>.
          <template v-if="impersonator"> (as {{ impersonator.name }})</template>
        </span>
        <button class="btn-ghost sm" :disabled="stoppingImpersonation" @click="stopImpersonating">Stop impersonating</button>
      </div>
      <div v-if="isImpersonatingOrganization" class="impersonation-banner org-imp-banner">
        <span>
          {{ $t('ui.orgImpersonation.banner') }}
          <b>{{ impersonatedOrganization && impersonatedOrganization.name }}</b>
          <template v-if="impersonatedOrganization && impersonatedOrganization.real_organization">
            ({{ $t('ui.orgImpersonation.as') }} {{ impersonatedOrganization.real_organization.name }})
          </template>
        </span>
        <button class="btn-ghost sm" :disabled="stoppingOrgImpersonation" @click="stopOrgImpersonation">
          {{ $t('ui.orgImpersonation.stop') }}
        </button>
      </div>
      <header class="topbar">
        <button class="topbar-hamburger" :aria-label="$t('ui.nav.openMenu')" @click="toggleMobileNav">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <line x1="3" y1="6"  x2="21" y2="6"/>
            <line x1="3" y1="12" x2="21" y2="12"/>
            <line x1="3" y1="18" x2="21" y2="18"/>
          </svg>
        </button>
        <div class="breadcrumbs">
          <template v-for="(seg, i) in breadcrumbs">
            <span :key="'s'+i" class="breadcrumb-sep" v-if="i > 0">/</span>
            <span :key="'b'+i" class="breadcrumb-item" :class="{active: i === breadcrumbs.length - 1}">
              <span v-if="i === breadcrumbs.length - 1" class="breadcrumb-dot"></span>{{ seg }}
            </span>
          </template>
        </div>

        <!-- Search bar — real catalog search -->
        <div class="topbar-search" ref="searchWrap">
          <svg class="topbar-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
          <input
            ref="searchInput"
            type="text"
            :placeholder="$t('ui.topbar.searchPlaceholder')"
            v-model="searchQuery"
            @focus="searchOpen=true"
            @keydown.escape="closeSearch"
            @keydown.enter="searchEnter"
          />
          <span class="topbar-search-kbd" v-if="!searchQuery" @click="$store.commit('SET_PALETTE', true)" style="cursor:pointer">Ctrl K</span>
          <button v-else class="topbar-search-clear" @click="clearSearch">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>

          <!-- Search dropdown -->
          <div class="search-dropdown" v-if="searchOpen && searchQuery.length > 0">
            <div v-if="searchResults.length === 0" class="search-empty">
              No tests match "<strong>{{ searchQuery }}</strong>"
            </div>
            <template v-else>
              <div class="search-section-label">Practice tests</div>
              <div v-for="r in searchResults" :key="r.id"
                   class="search-result"
                   @mousedown.prevent="pickSearchResult(r)">
                <div class="search-result-cover" :class="r.cover">
                  <img v-if="r.logo" :src="r.logo" :alt="r.vendor"
                       class="search-result-logo"
                       @error="$event.target.style.display='none'" />
                </div>
                <div class="search-result-body">
                  <div class="search-result-title">{{ r.title }}</div>
                  <div class="search-result-meta">{{ r.vendor }} · {{ r.items }} items · {{ r.time }}</div>
                </div>
                <span class="pill" :class="r.difficulty==='Beginner'?'success':r.difficulty==='Advanced'?'danger':'info'"
                      style="font-size:10.5px;flex-shrink:0">
                  {{ r.difficulty }}
                </span>
              </div>
            </template>
          </div>
        </div>

        <div class="topbar-actions">
          <!-- Group 1: Active organization switcher (locked on a tenant subdomain) -->
          <div class="topbar-group" v-if="currentOrganization">
            <div class="org-switch" ref="orgWrap">
              <button class="org-switch-btn" :class="{ locked: tenantLocked }"
                      @click.stop="!tenantLocked && (organizations.length > 1 || canImpersonateOrg) && (orgMenuOpen = !orgMenuOpen)"
                      :title="tenantLocked ? currentOrganization.name : $t('ui.topbar.switchOrganization')">
                <div class="org-switch-badge" :style="orgBadgeStyle(currentOrganization)">{{ orgInitials(currentOrganization.name) }}</div>
                <div class="org-switch-text">
                  <span class="org-switch-label">{{ $t('ui.topbar.organization') }}</span>
                  <span class="org-switch-name">{{ currentOrganization.name }}</span>
                </div>
                <svg v-if="tenantLocked" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" style="flex-shrink:0;opacity:0.55"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                <svg v-else-if="organizations.length > 1 || canImpersonateOrg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
                     style="transition:transform 0.15s;flex-shrink:0" :style="orgMenuOpen?'transform:rotate(180deg)':''"><polyline points="6 9 12 15 18 9"/></svg>
              </button>
              <div class="org-switch-dropdown" v-if="orgMenuOpen && !tenantLocked && (organizations.length > 1 || canImpersonateOrg)" @click.stop>
                <template v-if="organizations.length > 1">
                  <div class="org-switch-dropdown-label">{{ $t('ui.topbar.switchOrganization') }}</div>
                  <div v-for="org in organizations" :key="org.id"
                       class="org-switch-option"
                       :class="{active: org.id === currentOrganizationId}"
                       @click="switchOrg(org.id)">
                    <div class="org-switch-badge sm" :style="orgBadgeStyle(org)">{{ orgInitials(org.name) }}</div>
                    <div class="org-switch-option-info">
                      <div class="org-switch-option-name">{{ org.name }}</div>
                      <div class="org-switch-option-role">{{ (org.roles && org.roles.length) ? org.roles.join(', ') : 'Member' }}</div>
                    </div>
                    <svg v-if="switchingOrgId === org.id" class="org-switch-spin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" style="color:var(--accent);margin-left:auto;flex-shrink:0"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
                    <svg v-else-if="org.id === currentOrganizationId" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" style="color:var(--accent);margin-left:auto;flex-shrink:0"><polyline points="20 6 9 17 4 12"/></svg>
                  </div>
                </template>

                <!-- Organization impersonation (portal-app parity) -->
                <template v-if="canImpersonateOrg">
                  <div class="org-switch-dropdown-label org-imp-label">{{ $t('ui.orgImpersonation.title') }}</div>
                  <input
                    class="org-imp-search"
                    type="text"
                    v-model="orgImpQuery"
                    :placeholder="$t('ui.orgImpersonation.searchPlaceholder')"
                    @input="onOrgImpSearch"
                  />
                  <div v-if="orgImpLoading" class="org-imp-hint">{{ $t('common.loading') }}</div>
                  <div v-else-if="!orgImpResults.length" class="org-imp-hint">{{ $t('ui.orgImpersonation.noResults') }}</div>
                  <div v-else class="org-imp-list">
                    <div v-for="o in orgImpResults" :key="o.id"
                         class="org-switch-option"
                         @click="startOrgImpersonation(o.id)">
                      <div class="org-switch-badge sm" :style="orgBadgeStyle(o)">{{ orgInitials(o.name) }}</div>
                      <div class="org-switch-option-info">
                        <div class="org-switch-option-name">{{ o.name }}</div>
                      </div>
                      <svg v-if="startingOrgImpId === o.id" class="org-switch-spin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" style="color:var(--accent);margin-left:auto;flex-shrink:0"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </div>

          <!-- Group 2: Language selector · Settings · Notifications -->
          <div class="topbar-group">
            <!-- Language selector -->
            <div class="locale" ref="localeWrap" @click.stop="localeOpen=!localeOpen">
              <img :src="currentLocaleObj.flagImg" :alt="currentLocaleObj.label"
                   class="locale-flag-img"
                   @error="$event.target.style.display='none'" />
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" style="transition:transform 0.15s" :style="localeOpen?'transform:rotate(180deg)':''"><polyline points="6 9 12 15 18 9"/></svg>
              <div class="locale-dropdown" v-if="localeOpen" @click.stop>
                <div v-for="loc in locales" :key="loc.code"
                     class="locale-option"
                     :class="{active: loc.code === currentLocale}"
                     @click="selectLocale(loc.code)">
                  <img :src="loc.flagImg" :alt="loc.label" class="locale-option-flag-img"
                       @error="$event.target.style.display='none'" />
                  <span class="locale-option-label">{{ loc.label }}</span>
                  <svg v-if="loc.code === currentLocale" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                </div>
              </div>
            </div>
            <!-- Settings icon → account settings -->
            <button class="topbar-icon" title="Account settings" @click="$router.push('/account/profile')">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
            </button>
            <!-- Notification bell -->
            <button class="topbar-icon" title="Notifications" @click="$router.push('/notifications')">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
              <span class="badge" v-if="notifUnread > 0"></span>
            </button>
          </div>

          <!-- Group 3: Avatar with account dropdown -->
          <div class="topbar-group">
            <div class="avatar-wrap" ref="avatarWrap">
              <div class="avatar" @click.stop="avatarOpen=!avatarOpen">
                <img v-if="userPhoto" :src="userPhoto" alt="" class="avatar-img" />
                <template v-else>{{ userInitials }}</template>
              </div>
            <div class="avatar-menu" v-if="avatarOpen" @click.stop>
              <div class="avatar-menu-header">
                <div class="avatar-menu-avatar">
                  <img v-if="userPhoto" :src="userPhoto" alt="" class="avatar-img" />
                  <template v-else>{{ userInitials }}</template>
                </div>
                <div>
                  <div class="avatar-menu-name">{{ user.first_name }} {{ user.last_name }}</div>
                  <div class="avatar-menu-email">{{ user.email }}</div>
                </div>
              </div>
              <div class="avatar-menu-divider"></div>
              <button class="avatar-menu-item" @click="goToAccount('/account/profile')">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
                {{ $t('ui.account.profile') }}
              </button>
              <button class="avatar-menu-item" @click="goToAccount('/account/organization')">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 3H8a2 2 0 0 0-2 2v2h12V5a2 2 0 0 0-2-2z"/></svg>
                {{ $t('ui.account.organization') }}
              </button>
              <button class="avatar-menu-item" @click="goToAccount('/account/billing')">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>
                {{ $t('ui.account.billing') }}
              </button>
              <button class="avatar-menu-item" @click="goToAccount('/account/notifications')">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                {{ $t('ui.account.notifSettings') }}
              </button>
              <!-- Theme toggle -->
              <div class="avatar-menu-divider"></div>
              <div class="avatar-theme-row">
                <span class="avatar-theme-label">Appearance</span>
                <div class="avatar-theme-toggle">
                  <button class="avatar-theme-opt" :class="{active: theme==='default'}" @click="setTheme('default')">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
                    Light
                  </button>
                  <button class="avatar-theme-opt" :class="{active: theme==='dark'}" @click="setTheme('dark')">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
                    Dark
                  </button>
                </div>
              </div>
              <!-- Tokens -->
              <div class="avatar-menu-divider"></div>
              <div class="avatar-tokens-row">
                <div class="avatar-tokens-header">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style="color:var(--warning);flex-shrink:0"><circle cx="12" cy="12" r="10"/></svg>
                  <span class="avatar-tokens-label">IA Explanation Tokens</span>
                  <span class="avatar-tokens-count" :class="{ low: tokensLeft === 0 }">
                    <b>{{ tokensLeft.toLocaleString() }}</b> / {{ tokensTotal.toLocaleString() }}
                  </span>
                </div>
                <div class="avatar-tokens-track">
                  <div class="avatar-tokens-fill"
                       :style="{
                         width: tokensTotal > 0 ? Math.round(tokensLeft / tokensTotal * 100) + '%' : '0%',
                         background: tokensLeft === 0 ? 'var(--danger)' : tokensLeft / tokensTotal < 0.2 ? 'var(--warning)' : 'var(--accent)'
                       }"></div>
                </div>
                <div class="avatar-tokens-meta">
                  <template v-if="tokensLeft === 0">Out of tokens — purchase to keep using IA Explanation</template>
                  <template v-else>{{ tokensLeft.toLocaleString() }} of {{ tokensTotal.toLocaleString() }} tokens remaining</template>
                </div>
                <button class="avatar-tokens-buy" @click="openTokenPurchase">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  Buy more tokens
                </button>
              </div>
              <div class="avatar-menu-divider"></div>
              <button class="avatar-menu-item danger" @click="logout">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                {{ $t('ui.account.signOut') }}
              </button>
            </div>
            </div>
          </div>
        </div>
      </header>

      <main class="content" id="page" @click="closeSearch">
        <router-view />
      </main>
    </div>

    <!-- ── OVERLAYS ───────────────────────────────────────── -->
    <command-palette />
    <test-runner-overlay />
    <app-toast />
    <token-purchase-modal />
    <assessment-preview />
    <qti-import-modal />
    <ai-generate-modal />

    <!-- Reorder enterprise sections -->
    <transition name="reorder-fade">
      <div v-if="reorderOpen" class="reorder-overlay" role="dialog" aria-modal="true" aria-labelledby="reorder-title" @click.self="closeReorder">
        <div class="reorder-dialog">
          <header class="reorder-header">
            <div>
              <div id="reorder-title" class="reorder-title">{{ $t('ui.reorder.title') }}</div>
              <div class="reorder-subtitle">{{ $t('ui.reorder.hint') }}</div>
            </div>
            <button class="reorder-close" :title="$t('common.close')" @click="closeReorder">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </header>

          <div class="reorder-group">
            <div class="reorder-group-label">{{ $t('ui.reorder.groupProducts') }}</div>
            <ul class="reorder-list" role="list">
              <li v-for="(section, idx) in reorderDraftProducts" :key="'p-'+section.key"
                  class="reorder-row"
                  :class="{dragging: reorderDragGroup === 'product' && reorderDragKey === section.key}"
                  draggable="true"
                  @dragstart="onReorderDragStart($event, section.key, 'product')"
                  @dragover="onReorderDragOver($event, section.key, 'product')"
                  @drop="onReorderDrop($event, section.key, 'product')"
                  @dragend="onReorderDragEnd">
                <span class="reorder-handle" :aria-label="$t('ui.reorder.dragHandle')">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="9" cy="6" r="1.2"/><circle cx="15" cy="6" r="1.2"/><circle cx="9" cy="12" r="1.2"/><circle cx="15" cy="12" r="1.2"/><circle cx="9" cy="18" r="1.2"/><circle cx="15" cy="18" r="1.2"/></svg>
                </span>
                <span class="reorder-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" v-html="section.svg"></svg>
                </span>
                <span class="reorder-name">{{ $t(section.i18nKey) }}</span>
                <span class="reorder-position">{{ idx + 1 }}</span>
                <div class="reorder-move">
                  <button class="reorder-move-btn" :disabled="idx === 0" :aria-label="$t('ui.reorder.moveUp')" @click="moveReorderItem(section.key, -1, 'product')">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="18 15 12 9 6 15"/></svg>
                  </button>
                  <button class="reorder-move-btn" :disabled="idx === reorderDraftProducts.length - 1" :aria-label="$t('ui.reorder.moveDown')" @click="moveReorderItem(section.key, 1, 'product')">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="6 9 12 15 18 9"/></svg>
                  </button>
                </div>
              </li>
            </ul>
          </div>

          <div class="reorder-group" v-if="isEnterprise">
            <div class="reorder-group-label">{{ $t('ui.reorder.groupEnterprise') }}</div>
            <ul class="reorder-list" role="list">
              <li v-for="(section, idx) in reorderDraftSections" :key="'e-'+section.key"
                  class="reorder-row"
                  :class="{dragging: reorderDragGroup === 'enterprise' && reorderDragKey === section.key}"
                  draggable="true"
                  @dragstart="onReorderDragStart($event, section.key, 'enterprise')"
                  @dragover="onReorderDragOver($event, section.key, 'enterprise')"
                  @drop="onReorderDrop($event, section.key, 'enterprise')"
                  @dragend="onReorderDragEnd">
                <span class="reorder-handle" :aria-label="$t('ui.reorder.dragHandle')">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="9" cy="6" r="1.2"/><circle cx="15" cy="6" r="1.2"/><circle cx="9" cy="12" r="1.2"/><circle cx="15" cy="12" r="1.2"/><circle cx="9" cy="18" r="1.2"/><circle cx="15" cy="18" r="1.2"/></svg>
                </span>
                <span class="reorder-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" v-html="section.svg"></svg>
                </span>
                <span class="reorder-name">{{ $t(section.i18nKey) }}</span>
                <span class="reorder-position">{{ idx + 1 }}</span>
                <div class="reorder-move">
                  <button class="reorder-move-btn" :disabled="idx === 0" :aria-label="$t('ui.reorder.moveUp')" @click="moveReorderItem(section.key, -1, 'enterprise')">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="18 15 12 9 6 15"/></svg>
                  </button>
                  <button class="reorder-move-btn" :disabled="idx === reorderDraftSections.length - 1" :aria-label="$t('ui.reorder.moveDown')" @click="moveReorderItem(section.key, 1, 'enterprise')">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="6 9 12 15 18 9"/></svg>
                  </button>
                </div>
              </li>
            </ul>
          </div>

          <footer class="reorder-footer">
            <button class="reorder-reset" @click="resetReorderDraft">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>
              {{ $t('ui.reorder.resetDefault') }}
            </button>
            <div class="reorder-footer-actions">
              <button class="btn-ghost" @click="closeReorder">{{ $t('common.cancel') }}</button>
              <button class="btn-primary" @click="saveReorder">{{ $t('common.save') }}</button>
            </div>
          </footer>
        </div>
      </div>
    </transition>
  </div>
  </div>
</template>

<script>
import CommandPalette from './components/CommandPalette.vue';
import TestRunnerOverlay from './components/TestRunnerOverlay.vue';
import AppToast from './components/AppToast.vue';
import TokenPurchaseModal from './components/TokenPurchaseModal.vue';
import AssessmentPreview from './components/AssessmentPreview.vue';
import QtiImportModal from './components/QtiImportModal.vue';
import AiGenerateModal from './components/AiGenerateModal.vue';
import { CATALOG } from './data/catalog.js';
import {
  loadAppearance, applyTheme, applyFontSize, applyColorblind, applyAda,
} from './utils/appearance';

const ACCOUNT_ROUTES    = ['Profile', 'Organization', 'Billing', 'NotificationSettings'];
const DEVELOPER_ROUTES  = ['ApiReference', 'Webhooks'];
const AUTHORING_ROUTES  = ['Authoring', 'AuthoringDashboard'];
const CONTENT_ROUTES    = ['Content'];
const PARTNER_ROUTES    = ['Partner', 'PartnerReports', 'KeySupport',
  // --- B2B Comercio y partners (G02) ---
  'PartnerLicenses', 'PartnerKeys', 'KeyHistory', 'ResellerLimits',
  'Cart', 'Subscriptions', 'PurchaseHistory',
  'CatalogAdmin', 'CatalogLines', 'CatalogDistributions',
  'ProgressReport', 'SalesManagement',
  'CustomReports', 'BillingReport',
  'PricingAdmin', 'PricingCountriesTiers', 'PricingTierDiscounts', 'PricingRules', 'PricingRoyalties',
  'PartnerMembers', 'Subpartners', 'DealsPipeline',
  'NewsletterAdmin', 'ProctoringRoster',
  'HomeContentAdmin', 'HelpCenterAdmin', 'HelpCenter',
  // Fase 1: moved into the Partner Portal (reports overview/finance, IAM admin, LTI).
  'Reporting', 'Finance', 'Users', 'AdminOrganizations', 'IamRoles', 'LtiConnections', 'PartnerGroups'];
const ITALENTUP_ROUTES  = ['ItalentupHome', 'ItalentupEvaluations', 'ItalentupLibrary', 'ItalentupExaminees', 'ItalentupReports', 'ItalentupSettings', 'ItalentupSupport'];
const ENTERPRISE_ROUTES = ['Automation'];

const BREADCRUMB_MAP = {
  Dashboard:            ['Learn', 'Dashboard'],
  Catalog:              ['Learn', 'Catalog'],
  MyTests:              ['Learn', 'My Tests'],
  Library:              ['Learn', 'Library'],
  LearningPaths:        ['Learn', 'Learning Paths'],
  Results:              ['Learn', 'Results & Badges'],
  ResultDetail:         ['Learn', 'Results', 'Detail'],
  Keys:                 ['Learn', 'Keys'],
  LearnProductRegistration: ['Learn', 'Product Registration'],
  Authoring:            ['Authoring Tool', 'Assessment Editor'],
  AuthoringDashboard:   ['Authoring Tool', 'Dashboard'],
  Content:              ['Contenido'],
  Partner:              ['Partner Portal', 'Dashboard'],
  PartnerReports:       ['Partner Portal', 'Reports'],
  KeySupport:           ['Partner Portal', 'Keys', 'Support'],
  PartnerLicenses:      ['Partner Portal', 'Commerce', 'Licenses'],
  PartnerKeys:          ['Partner Portal', 'Keys'],
  KeyHistory:           ['Partner Portal', 'Keys', 'History'],
  ResellerLimits:       ['Partner Portal', 'Keys', 'Reseller limits'],
  Cart:                 ['Partner Portal', 'Commerce', 'Cart'],
  Subscriptions:        ['Partner Portal', 'Commerce', 'Subscriptions'],
  PurchaseHistory:      ['Partner Portal', 'Commerce', 'Purchases'],
  CatalogAdmin:         ['Partner Portal', 'Admin comercial', 'Catalog'],
  CatalogLines:         ['Partner Portal', 'Admin comercial', 'Catalog', 'Product lines'],
  CatalogDistributions: ['Partner Portal', 'Admin comercial', 'Catalog', 'Distributions'],
  ProgressReport:       ['Partner Portal', 'Reports', 'Progress'],
  SalesManagement:      ['Partner Portal', 'Reports', 'Sales'],
  CustomReports:        ['Partner Portal', 'Reports', 'Custom reports'],
  BillingReport:        ['Partner Portal', 'Reports', 'Billing'],
  Reporting:            ['Partner Portal', 'Reports', 'Overview'],
  Finance:              ['Partner Portal', 'Reports', 'Finance'],
  PricingAdmin:         ['Partner Portal', 'Admin comercial', 'Pricing'],
  PricingCountriesTiers:['Partner Portal', 'Admin comercial', 'Pricing', 'Countries & tiers'],
  PricingTierDiscounts: ['Partner Portal', 'Admin comercial', 'Pricing', 'Tier discounts'],
  PricingRules:         ['Partner Portal', 'Admin comercial', 'Pricing', 'Rules'],
  PricingRoyalties:     ['Partner Portal', 'Admin comercial', 'Pricing', 'Royalties'],
  PartnerMembers:       ['Partner Portal', 'Organization', 'Members'],
  Subpartners:          ['Partner Portal', 'Organization', 'Subpartners'],
  DealsPipeline:        ['Partner Portal', 'Organization', 'Deals'],
  Users:                ['Partner Portal', 'Organization', 'Team & Users'],
  AdminOrganizations:   ['Partner Portal', 'Organization', 'Organizations'],
  IamRoles:             ['Partner Portal', 'Organization', 'Roles & Permissions'],
  NewsletterAdmin:      ['Partner Portal', 'Content', 'Newsletter'],
  ProctoringRoster:     ['Partner Portal', 'Content', 'Proctoring'],
  HomeContentAdmin:     ['Partner Portal', 'Content', 'Home content'],
  HelpCenterAdmin:      ['Partner Portal', 'Content', 'Help center'],
  HelpCenter:           ['Partner Portal', 'Content', 'Help center'],
  LtiConnections:       ['Partner Portal', 'Content', 'LTI Connections'],
  ItalentupHome:        ['Assessment', 'Home'],
  ItalentupEvaluations: ['Assessment', 'Evaluations'],
  ItalentupLibrary:     ['Assessment', 'Test library'],
  ItalentupExaminees:   ['Assessment', 'Examinees'],
  ItalentupReports:     ['Assessment', 'Reports'],
  ItalentupSettings:    ['Assessment', 'Settings'],
  ItalentupSupport:     ['Assessment', 'Support'],
  Profile:              ['Account Settings', 'Profile'],
  Organization:         ['Account Settings', 'Organization'],
  Billing:              ['Account Settings', 'Billing'],
  NotificationSettings: ['Account Settings', 'Notifications'],
  AccountSecurity:      ['Account Settings', 'Security'],
  AccountSessions:      ['Account Settings', 'Sessions'],
  AccountPreferences:   ['Account Settings', 'Preferences'],
  AccountStub:          ['Account Settings'],
  NotificationsView:    ['Notifications'],
  Automation:           ['Enterprise', 'Automation'],
  ApiReference:         ['Developer', 'API Reference'],
  Webhooks:             ['Developer', 'Webhooks'],
};

const ALL_NAV_ITEMS = {
  learn: [
    // Learn NUEVO (learn-app-next) embebido via iframe (ver LearnEmbed.vue +
    // router: /learn/library|reports|badges). Reemplaza a los items viejos
    // (Dashboard/Catalog/My Tests/Library/Learning Paths/Results/Keys), que
    // siguen existiendo como rutas pero ya no aparecen en el menu.
    { to: '/learn/library', label: 'Training Library', i18nKey: 'nav.trainingLibrary', exact: true },
    { to: '/learn/reports', label: 'Reports',          i18nKey: 'nav.reports' },
    { to: '/learn/badges',  label: 'Badges',           i18nKey: 'nav.badges' },
    { to: '/learn/product-registration', label: 'Product Registration', i18nKey: 'nav.productRegistration', perm: { action: 'learn:ViewKeys', resource: 'learn:key:*' } },
  ],
  authoring: [
    { to: '/authoring',        label: 'Dashboard',         i18nKey: 'ui.nav.authoringDashboard' },
    { to: '/authoring/editor', label: 'Assessment Editor', i18nKey: 'ui.nav.assessmentEditor' },
  ],
  // contenidoMis (Blueprints Coverage Analysis) embebida; ver ContentWorkspace.vue.
  content: [
    { to: '/content',                  label: 'Dashboard',          i18nKey: 'ui.nav.contentDashboard', exact: true },
    { to: '/content/hub',              label: 'Quality hub',        i18nKey: 'ui.nav.contentHub' },
    { to: '/content/asistente',        label: 'AI assistant',       i18nKey: 'ui.nav.contentAssistant' },
    { to: '/content/cobertura',        label: 'Coverage',           i18nKey: 'ui.nav.contentCoverage' },
    { to: '/content/generacion',       label: 'Generation',         i18nKey: 'ui.nav.contentGeneration' },
    { to: '/content/importar',         label: 'Import bank',        i18nKey: 'ui.nav.contentImport' },
    { to: '/content/preguntas',        label: 'Authoring questions', i18nKey: 'ui.nav.contentQuestions' },
    { to: '/content/tests-publicados', label: 'Published tests',    i18nKey: 'ui.nav.contentPublishedTests' },
  ],
  // Fase 2 (IA/UX overhaul): el Portal ya no es una lista plana. Los enlaces
  // se agrupan bajo cabeceras de intención (grupos {heading, items}). Tras las
  // fusiones de Fase 3 el set de hojas se reduce: Claves e Informes son ahora
  // un único workspace con pestañas; Ventas vive como pestaña dentro de
  // Informes; Miembros/Subpartners comparten el workspace de Organización.
  partner: [
    {
      heading: 'ui.nav.groups.home',
      items: [
        { to: '/partner', label: 'Dashboard', i18nKey: 'ui.nav.partnerDashboard' },
      ],
    },
    {
      heading: 'ui.nav.groups.commerce',
      items: [
        { to: '/partner/commerce/licenses',      label: 'Licenses',      i18nKey: 'partner.licenses.nav' },
        { to: '/partner/commerce/cart',          label: 'Cart',          i18nKey: 'partner.cart.nav', perm: { action: 'partner_portal:PurchaseKeys', resource: 'partner_portal:cart:*' } },
        { to: '/partner/commerce/subscriptions', label: 'Subscriptions', i18nKey: 'partner.subscriptions.nav', perm: { action: 'partner_portal:ManageSubscriptions', resource: 'partner_portal:subscription:*' } },
        { to: '/partner/commerce/purchases',     label: 'Purchases',     i18nKey: 'partner.purchases.nav', perm: { action: 'partner_portal:ViewFinance', resource: 'partner_portal:finance:*' } },
      ],
    },
    {
      heading: 'ui.nav.groups.keys',
      items: [
        // Workspace de claves (pestañas: Todas / Por lote / Soporte / Cupos).
        { to: '/partner/keys', label: 'Keys', i18nKey: 'partner.keys.nav' },
      ],
    },
    {
      heading: 'ui.nav.groups.reports',
      items: [
        // Hub de informes (pestañas: Resumen / Progreso / Resultados / Custom /
        // Facturación / Ventas / Informe de ventas). Ventas ya no es hoja aparte.
        { to: '/partner/reports', label: 'Reports', i18nKey: 'ui.nav.partnerReports' },
      ],
    },
    {
      heading: 'ui.nav.groups.adminCommerce',
      items: [
        { to: '/partner/admin/pricing', label: 'Pricing',  i18nKey: 'partner.pricing.nav', perm: { action: 'partner_portal:ManagePricing', resource: 'partner_portal:pricing:*' } },
        { to: '/partner/admin/catalog', label: 'Catalog',  i18nKey: 'catalog.nav' },
      ],
    },
    {
      heading: 'ui.nav.groups.organization',
      items: [
        // Workspace de organización (pestañas: Miembros / Subpartners).
        { to: '/partner/org/members',       label: 'Organization',        i18nKey: 'partner.orgWorkspace.nav' },
        { to: '/partner/org/deals',         label: 'Deals',               i18nKey: 'partner.deals.nav' },
        { to: '/partner/org/users',         label: 'Team & Users',        i18nKey: 'ui.nav.teamUsers', perm: { action: 'partner_portal:ManageUsers', resource: 'partner_portal:user:*' } },
        { to: '/partner/org/groups',        label: 'Groups',              i18nKey: 'ui.nav.groupsCohorts', perm: { action: 'partner_portal:ManageGroups', resource: 'partner_portal:group:*' } },
        { to: '/partner/org/organizations', label: 'Organizations',       i18nKey: 'ui.nav.organizations', perm: { action: 'partner_portal:ManageOrganizations', resource: 'partner_portal:organization:*' } },
        { to: '/partner/org/roles',         label: 'Roles & Permissions', i18nKey: 'ui.nav.rolesPermissions', perm: { action: 'partner_portal:ManageRoles', resource: 'partner_portal:role:*' } },
      ],
    },
    {
      heading: 'ui.nav.groups.content',
      items: [
        { to: '/partner/content/home',       label: 'Home content',    i18nKey: 'experience.home.nav' },
        { to: '/partner/content/help',       label: 'Help center',     i18nKey: 'experience.help.nav' },
        { to: '/partner/content/newsletter', label: 'Newsletter',      i18nKey: 'newsletter.nav', perm: { action: 'partner_portal:ManageNewsletter', resource: 'partner_portal:newsletter:*' } },
        { to: '/partner/content/proctoring', label: 'Proctoring',      i18nKey: 'proctoring.nav', perm: { action: 'partner_portal:ManageProctoring', resource: 'partner_portal:proctoring:*' } },
        { to: '/partner/content/lti',        label: 'LTI Connections', i18nKey: 'ui.nav.ltiConnections' },
      ],
    },
  ],
  // iTalentUp (italentup-app-next, Vue 3) embebido via iframe (ver
  // ItalentupEmbed.vue + router: /italentup/*).
  italentup: [
    { to: '/italentup/home',        label: 'Home',         i18nKey: 'ui.nav.italentupHome' },
    { to: '/italentup/evaluations', label: 'Evaluations',  i18nKey: 'ui.nav.italentupEvaluations' },
    { to: '/italentup/library',     label: 'Test library', i18nKey: 'ui.nav.italentupLibrary' },
    { to: '/italentup/examinees',   label: 'Examinees',    i18nKey: 'ui.nav.italentupExaminees' },
    { to: '/italentup/reports',     label: 'Reports',      i18nKey: 'ui.nav.italentupReports', perm: { action: 'italentup:ViewReports', resource: 'italentup:report:*' } },
    { to: '/italentup/settings',    label: 'Settings',     i18nKey: 'ui.nav.italentupSettings', perm: { action: 'italentup:ManageSettings', resource: 'italentup:settings:*' } },
    { to: '/italentup/support',     label: 'Support',      i18nKey: 'ui.nav.italentupSupport' },
  ],
  account: [
    { to: '/account/profile',       label: 'Profile',               i18nKey: 'ui.account.profile' },
    { to: '/account/organization',  label: 'Organization',          i18nKey: 'ui.account.organization' },
    { to: '/account/billing',       label: 'Billing',               i18nKey: 'ui.account.billing' },
    { to: '/account/notifications', label: 'Notification Settings', i18nKey: 'ui.account.notifSettings' },
  ],
  developer: [
    { to: '/developer/api',      label: 'API Reference',   i18nKey: 'ui.nav.apiReference' },
    { to: '/developer/webhooks', label: 'Webhooks',        i18nKey: 'ui.nav.webhooks' },
  ],
  automation: [
    { to: '/automation', label: 'Workflows', i18nKey: 'ui.nav.workflows' },
  ],
};

// A sidebar section's item list can be EITHER a flat array of leaf items
// (`[{to,i18nKey,...}]`) or grouped blocks (`[{heading, items:[...]}]`). Fase 2
// introduced grouping for the Partner Portal (intent headers). These helpers
// let the nav render both shapes uniformly.
function isNavGroup(entry) {
  return !!entry && Array.isArray(entry.items) && 'heading' in entry;
}
// Normalize a section into grouped blocks. Flat sections become one headingless
// group so the double `v-for` in the flyout works for every section.
function normalizeNavGroups(section) {
  const list = section || [];
  if (list.length && isNavGroup(list[0])) return list;
  return [{ heading: null, items: list }];
}
// Flatten a section into its leaf items regardless of grouping (favorites,
// "click section → first destination", etc.).
function flattenNavItems(section) {
  return normalizeNavGroups(section).reduce((acc, group) => acc.concat(group.items), []);
}

// Product sidebar sections (top "Products" group). Order here is the factory
// default; users can re-arrange it via the Reorder dialog in the sidebar footer
// (persisted to localStorage under `mpts.product.order`). Sections flagged
// `enterpriseOnly` are only rendered when the active org grants enterprise access.
const PRODUCT_SECTIONS = [
  {
    key: 'italentup',
    i18nKey: 'ui.nav.italentup',
    itemsKey: 'italentup',
    hasFavorites: true,
    enterpriseOnly: false,
    requires: 'italentup',
    activeRoutes: ITALENTUP_ROUTES,
    svg: '<polyline points="7 17 17 7"/><polyline points="7 7 17 7 17 17"/>',
  },
  {
    key: 'learn',
    i18nKey: 'ui.nav.learn',
    itemsKey: 'learn',
    hasFavorites: true,
    enterpriseOnly: false,
    activeRoutes: ['LearnLibrary', 'LearnReports', 'LearnBadges', 'LearnProductRegistration'],
    svg: '<rect x="3" y="3" width="7" height="9" rx="1"/><rect x="14" y="3" width="7" height="5" rx="1"/><rect x="14" y="12" width="7" height="9" rx="1"/><rect x="3" y="16" width="7" height="5" rx="1"/>',
  },
  {
    key: 'authoring',
    i18nKey: 'ui.nav.authoring',
    itemsKey: 'authoring',
    hasFavorites: false,
    enterpriseOnly: true,
    activeRoutes: AUTHORING_ROUTES,
    svg: '<path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 1 1 3 3L7 19l-4 1 1-4Z"/>',
  },
  {
    key: 'content',
    i18nKey: 'ui.nav.content',
    itemsKey: 'content',
    hasFavorites: false,
    enterpriseOnly: true,
    // Además de enterprise: permiso content:Access y org raíz MeasureUp.
    requires: 'content',
    activeRoutes: CONTENT_ROUTES,
    svg: '<path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>',
  },
  {
    key: 'partner',
    i18nKey: 'ui.nav.partnerPortal',
    itemsKey: 'partner',
    hasFavorites: true,
    enterpriseOnly: true,
    cascade: true,
    activeRoutes: PARTNER_ROUTES,
    svg: '<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>',
  },
];
const DEFAULT_PRODUCT_ORDER = PRODUCT_SECTIONS.map(s => s.key);

function loadProductOrder() {
  try {
    // Legacy key retired when Assessment was moved above Learn (new default order).
    localStorage.removeItem('mpts.product.order');
    const saved = JSON.parse(localStorage.getItem('mpts.product.order.v2') || 'null');
    if (!Array.isArray(saved)) return DEFAULT_PRODUCT_ORDER.slice();
    // Sections added after the order was saved are appended, so the user's
    // arrangement survives new product sections.
    const valid =
      saved.every(k => DEFAULT_PRODUCT_ORDER.includes(k)) &&
      new Set(saved).size === saved.length;
    if (!valid) return DEFAULT_PRODUCT_ORDER.slice();
    return [...saved, ...DEFAULT_PRODUCT_ORDER.filter(k => !saved.includes(k))];
  } catch {
    return DEFAULT_PRODUCT_ORDER.slice();
  }
}

// Enterprise-only sidebar sections. Order here is the factory default; users can
// re-arrange it via the Reorder dialog in the sidebar footer (persisted to
// localStorage under `mpts.enterprise.order`).
const ENTERPRISE_SECTIONS = [
  {
    key: 'developer',
    i18nKey: 'ui.nav.developer',
    itemsKey: 'developer',
    hasFavorites: true,
    activeRoutes: ['ApiReference', 'Webhooks'],
    svg: '<polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>',
  },
  {
    key: 'automation',
    i18nKey: 'ui.nav.automation',
    itemsKey: 'automation',
    hasFavorites: false,
    activeRoutes: ['Automation'],
    svg: '<rect x="3" y="3" width="18" height="18" rx="2"/><path d="M7 8h10M7 12h6m-6 4h4"/>',
  },
];
const DEFAULT_ENTERPRISE_ORDER = ENTERPRISE_SECTIONS.map(s => s.key);

function loadEnterpriseOrder() {
  try {
    const saved = JSON.parse(localStorage.getItem('mpts.enterprise.order') || 'null');
    if (!Array.isArray(saved)) return DEFAULT_ENTERPRISE_ORDER.slice();
    // Accept only if it's a permutation of the current default set
    const valid =
      saved.length === DEFAULT_ENTERPRISE_ORDER.length &&
      saved.every(k => DEFAULT_ENTERPRISE_ORDER.includes(k)) &&
      new Set(saved).size === saved.length;
    return valid ? saved.slice() : DEFAULT_ENTERPRISE_ORDER.slice();
  } catch {
    return DEFAULT_ENTERPRISE_ORDER.slice();
  }
}

export default {
  name: 'App',
  components: { CommandPalette, TestRunnerOverlay, AppToast, TokenPurchaseModal, AssessmentPreview, QtiImportModal, AiGenerateModal },
  data() {
    return {
      sidebarCollapsed: localStorage.getItem('mpts.sidebar.collapsed') === 'true',
      mobileNavOpen: false,
      stoppingImpersonation: false,
      // Drawer tier covers phone + tablet (≤ 1024 px). At this width the
      // sidebar is hidden until the user opens it via the hamburger.
      isMobileViewport: typeof window !== 'undefined' && window.innerWidth <= 1024,
      enterpriseOrder: loadEnterpriseOrder(),
      productOrder: loadProductOrder(),
      reorderOpen: false,
      reorderDraft: [],
      productDraft: [],
      reorderDragKey: null,
      reorderDragGroup: null,
      openSection: 'learn',
      // Viewport-relative top for the hover-flyout submenu panel (position:fixed).
      flyoutTop: 56,
      // Hover-intent state for the level-2 cascade subpanels. `openGroupId`
      // holds the id of the group whose subpanel is kept open by JS (so the
      // pointer can traverse the diagonal gap without the panel collapsing).
      // `groupCloseTimer` is the pending close timeout, cleared on re-enter.
      openGroupId: null,
      groupCloseTimer: null,
      favorites: (() => { try { return JSON.parse(localStorage.getItem('mpts.favorites') || '[]'); } catch { return []; } })(),
      logoFailed: false,
      iconFailed: false,
      searchQuery: '',
      searchOpen: false,
      catalog: CATALOG,
      localeOpen: false,
      avatarOpen: false,
      currentLocale: 'en-US',
      locales: [
        { code: 'en-US', i18nCode: 'en', flag: '🇺🇸', flagImg: 'https://flagcdn.com/w20/us.png', label: 'English (US)' },
        { code: 'es-ES', i18nCode: 'es', flag: '🇪🇸', flagImg: 'https://flagcdn.com/w20/es.png', label: 'Español (ES)' },
        { code: 'fr-FR', i18nCode: 'fr', flag: '🇫🇷', flagImg: 'https://flagcdn.com/w20/fr.png', label: 'Français (FR)' },
        { code: 'de-DE', i18nCode: 'de', flag: '🇩🇪', flagImg: 'https://flagcdn.com/w20/de.png', label: 'Deutsch (DE)' },
        { code: 'pt-BR', i18nCode: 'pt', flag: '🇧🇷', flagImg: 'https://flagcdn.com/w20/br.png', label: 'Português (BR)' },
        { code: 'ja-JP', i18nCode: 'ja', flag: '🇯🇵', flagImg: 'https://flagcdn.com/w20/jp.png', label: '日本語' },
        { code: 'zh-CN', i18nCode: 'zh', flag: '🇨🇳', flagImg: 'https://flagcdn.com/w20/cn.png', label: '中文 (简体)' },
        { code: 'ko-KR', i18nCode: 'ko', flag: '🇰🇷', flagImg: 'https://flagcdn.com/w20/kr.png', label: '한국어' },
      ],
      switchingOrgId: null,
      orgMenuOpen: false,
      notifUnread: 3,
      theme: loadAppearance().theme,
      stoppingOrgImpersonation: false,
      orgImpQuery: '',
      orgImpResults: [],
      orgImpLoading: false,
      startingOrgImpId: null,
      orgImpSearchTimer: null,
    };
  },
  computed: {
    user()         { return this.$store.getters['auth/user'] || { first_name: '', last_name: '', email: '' }; },
    isImpersonating() { return this.$store.getters['auth/isImpersonating']; },
    impersonator()    { return this.$store.getters['auth/impersonator']; },
    isImpersonatingOrganization() { return this.$store.getters['auth/isImpersonatingOrganization']; },
    impersonatedOrganization()    { return this.$store.getters['auth/impersonatedOrganization']; },
    canImpersonateOrg() {
      return this.$store.getters['auth/can']('partner_portal:ImpersonateOrganization', 'partner_portal:organization:*');
    },
    tokensLeft()   { return this.$store.state.tokens.left; },
    tokensTotal()  { return this.$store.state.tokens.total; },
    userInitials() {
      const u = this.user || {};
      return ((u.first_name?.[0] ?? '') + (u.last_name?.[0] ?? '')).toUpperCase() || 'U';
    },
    userPhoto() {
      return (this.user && this.user.profile_photo) || null;
    },
    breadcrumbs() {
      return BREADCRUMB_MAP[this.$route.name] || ['Learn', 'Dashboard'];
    },
    activeProduct() {
      const n = this.$route.name;
      if (AUTHORING_ROUTES.includes(n)) return 'authoring';
      if (CONTENT_ROUTES.includes(n))   return 'content';
      if (PARTNER_ROUTES.includes(n))   return 'partner';
      if (ITALENTUP_ROUTES.includes(n)) return 'italentup';
      return 'learn';
    },

    // Enterprise/partner UI is shown based on real IAM permissions in the
    // ACTIVE organization, not a hardcoded account id.
    isEnterprise() {
      return this.$store.getters['auth/can']('partner_portal:ViewDashboard', 'partner_portal:*:*')
        || this.$store.getters['auth/can']('authoring:Access', 'authoring:*:*');
    },
    // Tab "Contenido": sólo en la organización raíz MeasureUp (los Administrators
    // de organizaciones hijas también tienen *:*, el permiso no basta).
    canSeeContent() {
      const org = this.currentOrganization;
      return !!(org && org.is_measureup_root)
        && this.$store.getters['auth/can']('content:Access', 'content:*:*');
    },
    organizations()         { return this.$store.getters['auth/organizations'] || []; },
    currentOrganizationId() { return this.$store.getters['auth/currentOrganizationId']; },
    currentOrganization()   { return this.$store.getters['auth/currentOrganization']; },
    // Multitenant PLS: on a dedicated org subdomain the session is locked to that
    // tenant, so the organization switcher is shown read-only.
    tenantLocked()          { return !!this.$store.getters['branding/tenant']; },

    // White-label (PLS) logo/name for the sidebar. Falls back to the MeasureUp
    // default logo when no tenant branding is present; the text wordmark is used
    // only if the image fails to load (@error → logoFailed).
    brandLogoUrl()          { return this.$store.getters['branding/logoUrl'] || 'https://www.measureup.com/media/logo/default/logo_measureup_1_.png'; },
    brandName()             { return this.$store.getters['branding/orgName'] || 'MeasureUp'; },

    learnItems()      { return ALL_NAV_ITEMS.learn; },
    authoringItems()  { return ALL_NAV_ITEMS.authoring; },
    partnerItems()    { return ALL_NAV_ITEMS.partner; },
    italentupItems()  { return ALL_NAV_ITEMS.italentup; },
    accountItems()    { return ALL_NAV_ITEMS.account; },
    developerItems()  { return ALL_NAV_ITEMS.developer; },
    reportingItems()  { return ALL_NAV_ITEMS.reporting; },
    automationItems() { return ALL_NAV_ITEMS.automation; },
    financeItems()    { return ALL_NAV_ITEMS.finance; },

    orderedProductSections() {
      const byKey = Object.fromEntries(PRODUCT_SECTIONS.map(s => [s.key, s]));
      const picked = this.productOrder.map(k => byKey[k]).filter(Boolean);
      const missing = PRODUCT_SECTIONS.filter(s => !this.productOrder.includes(s.key));
      return [...picked, ...missing].filter(s => this.canSeeProductSection(s));
    },
    reorderDraftProducts() {
      const byKey = Object.fromEntries(PRODUCT_SECTIONS.map(s => [s.key, s]));
      return this.productDraft
        .map(k => byKey[k])
        .filter(Boolean)
        .filter(s => this.canSeeProductSection(s));
    },

    orderedEnterpriseSections() {
      // Map user-saved order to the section configs. Any keys that somehow
      // dropped out (future-config change) are appended at the end in default order.
      const byKey = Object.fromEntries(ENTERPRISE_SECTIONS.map(s => [s.key, s]));
      const picked = this.enterpriseOrder.map(k => byKey[k]).filter(Boolean);
      const missing = ENTERPRISE_SECTIONS.filter(s => !this.enterpriseOrder.includes(s.key));
      return [...picked, ...missing];
    },
    reorderDraftSections() {
      const byKey = Object.fromEntries(ENTERPRISE_SECTIONS.map(s => [s.key, s]));
      return this.reorderDraft.map(k => byKey[k]).filter(Boolean);
    },

    favoritedItems() {
      const allItems = Object.values(ALL_NAV_ITEMS).reduce(
        (acc, section) => acc.concat(flattenNavItems(section)), []
      );
      return allItems.filter(item => this.favorites.includes(item.to));
    },

    searchResults() {
      if (!this.searchQuery.trim()) return [];
      const q = this.searchQuery.toLowerCase();
      return this.catalog.filter(c =>
        c.title.toLowerCase().includes(q) ||
        c.vendor.toLowerCase().includes(q) ||
        c.id.toLowerCase().includes(q)
      ).slice(0, 6);
    },
    currentLocaleObj() {
      return this.locales.find(l => l.code === this.currentLocale) || this.locales[0];
    },
    isStandaloneRoute() {
      return ['Login', 'Register', 'SelectOrganization'].includes(this.$route.name);
    },
    flyoutStyle() {
      return { top: this.flyoutTop + 'px' };
    },
  },
  watch: {
    $route(to) {
      if (ACCOUNT_ROUTES.includes(to.name))         this.openSection = 'account';
      else if (DEVELOPER_ROUTES.includes(to.name))  this.openSection = 'developer';
      else if (AUTHORING_ROUTES.includes(to.name))  this.openSection = 'authoring';
      else if (CONTENT_ROUTES.includes(to.name))    this.openSection = 'content';
      else if (PARTNER_ROUTES.includes(to.name))    this.openSection = 'partner';
      else if (ITALENTUP_ROUTES.includes(to.name))  this.openSection = 'italentup';
      else if (to.name === 'Automation')             this.openSection = 'automation';
      else                                           this.openSection = 'learn';
      this.closeSearch();
      this.closeMobileNav();
    },
    orgMenuOpen(open) {
      // Prefetch impersonable orgs the first time the org menu opens (only if
      // the user can impersonate organizations).
      if (open && this.canImpersonateOrg && !this.orgImpResults.length) {
        this.loadOrgImpResults();
      }
    },
    mobileNavOpen(open) {
      // iOS-safe scroll lock. `overflow: hidden` on body re-anchors
      // `position: fixed` descendants on iOS Safari (a long-standing WebKit
      // bug) — and the drawer is fixed, so we can't use that approach.
      // Instead, pin the body itself in place at the user's scroll offset
      // and restore the offset on close.
      if (typeof document === 'undefined') return;
      const body = document.body;
      if (open) {
        this._scrollY = window.scrollY;
        body.style.position = 'fixed';
        body.style.top      = `-${this._scrollY}px`;
        body.style.left     = '0';
        body.style.right    = '0';
        body.style.width    = '100%';
      } else {
        const y = this._scrollY ?? 0;
        body.style.position = '';
        body.style.top      = '';
        body.style.left     = '';
        body.style.right    = '';
        body.style.width    = '';
        window.scrollTo(0, y);
      }
    },
  },
  mounted() {
    window.addEventListener('keydown', this.onKeyDown);
    document.addEventListener('click', this.onDocClick);
    window.addEventListener('resize', this.onResize);
    this.$root.$on('mu:shell-locale', this.applyShellLocaleFromLearn);
    this.applyStoredAppearance();
    this.syncLocaleFromUser();
    const n = this.$route.name;
    if (ACCOUNT_ROUTES.includes(n))         this.openSection = 'account';
    else if (DEVELOPER_ROUTES.includes(n))  this.openSection = 'developer';
    else if (AUTHORING_ROUTES.includes(n))  this.openSection = 'authoring';
    else if (CONTENT_ROUTES.includes(n))    this.openSection = 'content';
    else if (PARTNER_ROUTES.includes(n))    this.openSection = 'partner';
    else if (ITALENTUP_ROUTES.includes(n))  this.openSection = 'italentup';
    else if (n === 'Automation')            this.openSection = 'automation';
    else                                   this.openSection = 'learn';

    // A11: el saldo de tokens del Tutor IA es server-side; lo cargamos al montar
    // para que el topbar refleje la fuente de verdad (no localStorage). Degrada
    // en silencio si el usuario no tiene el permiso learn:UseAiTutor.
    if (this.$store.getters['auth/isLoggedIn']) {
      this.$store.dispatch('fetchTokens');
    }
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.onKeyDown);
    document.removeEventListener('click', this.onDocClick);
    window.removeEventListener('resize', this.onResize);
    this.$root.$off('mu:shell-locale', this.applyShellLocaleFromLearn);
    if (this.groupCloseTimer) { clearTimeout(this.groupCloseTimer); this.groupCloseTimer = null; }
    if (typeof document !== 'undefined') {
      const body = document.body;
      body.style.position = '';
      body.style.top      = '';
      body.style.left     = '';
      body.style.right    = '';
      body.style.width    = '';
      body.style.overflow = '';
    }
  },
  methods: {
    toggleSidebar() {
      this.sidebarCollapsed = !this.sidebarCollapsed;
      localStorage.setItem('mpts.sidebar.collapsed', this.sidebarCollapsed);
    },
    toggleMobileNav() { this.mobileNavOpen = !this.mobileNavOpen; },
    closeMobileNav()  { this.mobileNavOpen = false; },
    goHome() {
      // Tapping the logo closes the drawer first (UX expectation on phones).
      this.closeMobileNav();
      if (this.$route.path !== '/dashboard') this.$router.push('/dashboard').catch(() => {});
    },
    onResize() {
      const wasMobile = this.isMobileViewport;
      this.isMobileViewport = window.innerWidth <= 1024;
      // Auto-close the drawer if the viewport grows past the touch tier
      // (e.g. user rotates a tablet to landscape, or resizes a desktop
      // window during devtools work).
      if (wasMobile && !this.isMobileViewport && this.mobileNavOpen) {
        this.mobileNavOpen = false;
      }
    },
    toggleSection(key) {
      // Desktop: sections open as hover-flyouts, so a click navigates straight
      // to the section's first destination (HubSpot behaviour). Mobile drawer
      // keeps the accordion toggle since hover isn't available on touch.
      if (this.isMobileViewport) {
        this.openSection = this.openSection === key ? null : key;
        return;
      }
      const first = flattenNavItems(ALL_NAV_ITEMS[key])[0];
      if (!first) {
        this.openSection = this.openSection === key ? null : key;
        return;
      }
      if (first.to) {
        if (this.$route.path !== first.to) this.$router.push(first.to).catch(() => {});
      } else if (first.href) {
        window.location.href = first.href;
      }
    },
    onSectionHover(e) {
      if (this.isMobileViewport) return;
      const section = e.currentTarget;
      const rect = section.getBoundingClientRect();
      let top = rect.top;
      this.flyoutTop = top;
      // Clamp so panels near the bottom of the list stay fully on screen.
      this.$nextTick(() => {
        const panel = section.querySelector('.nav-subitems');
        if (!panel) return;
        const h = panel.offsetHeight;
        const maxTop = window.innerHeight - h - 12;
        if (top > maxTop) this.flyoutTop = Math.max(8, maxTop);
      });
    },
    // Vertical clamp for the level-2 cascade subpanel: if the panel (anchored
    // at the hovered group row) would overflow the viewport bottom, nudge it
    // upward. Mirrors onSectionHover but operates on the cascading subpanel.
    // Also drives the JS hover-intent state so the subpanel stays open while
    // the pointer traverses the diagonal gap toward it.
    onGroupHover(e) {
      if (this.isMobileViewport) return;
      const group = e.currentTarget;
      // Keep this group's subpanel open; cancel any pending close.
      if (this.groupCloseTimer) {
        clearTimeout(this.groupCloseTimer);
        this.groupCloseTimer = null;
      }
      this.openGroupId = group.dataset.groupId || null;
      this.$nextTick(() => {
        const panel = group.querySelector(':scope > .nav-subpanel');
        if (!panel) return;
        panel.style.top = '0px';
        const rect = panel.getBoundingClientRect();
        const overflow = rect.bottom - (window.innerHeight - 12);
        if (overflow > 0) {
          panel.style.top = `-${Math.min(overflow, rect.top - 12)}px`;
        }
      });
    },
    // Hover-intent close: schedule a delayed close so a momentary un-hover
    // (crossing the diagonal from the row to the subpanel, or between the two
    // panels) does NOT instantly collapse the cascade. Re-entering any part of
    // the group (onGroupHover) cancels this timer.
    onGroupLeave() {
      if (this.isMobileViewport) return;
      if (this.groupCloseTimer) clearTimeout(this.groupCloseTimer);
      this.groupCloseTimer = setTimeout(() => {
        this.openGroupId = null;
        this.groupCloseTimer = null;
      }, 200);
    },
    // Leaving the whole level-1 section flyout also schedules the cascade
    // close (with the same tolerance) so an open level-2 subpanel does not
    // linger once the pointer abandons the menu entirely.
    onSectionLeave() {
      this.onGroupLeave();
    },
    toggleFavorite(path) {
      const idx = this.favorites.indexOf(path);
      if (idx === -1) {
        this.favorites.push(path);
      } else {
        this.favorites.splice(idx, 1);
      }
      localStorage.setItem('mpts.favorites', JSON.stringify(this.favorites));
    },
    onKeyDown(e) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        this.$store.commit('SET_PALETTE', true);
      }
      if (e.key === 'Escape') {
        if (this.reorderOpen) { this.closeReorder(); return; }
        if (this.mobileNavOpen) { this.closeMobileNav(); return; }
        this.closeSearch();
      }
    },
    onDocClick(e) {
      if (this.$refs.searchWrap && !this.$refs.searchWrap.contains(e.target)) this.closeSearch();
      if (this.$refs.localeWrap && !this.$refs.localeWrap.contains(e.target)) this.localeOpen = false;
      if (this.$refs.avatarWrap && !this.$refs.avatarWrap.contains(e.target)) this.avatarOpen = false;
      if (this.$refs.orgWrap && !this.$refs.orgWrap.contains(e.target)) this.orgMenuOpen = false;
    },
    closeSearch() {
      this.searchOpen = false;
    },
    clearSearch() {
      this.searchQuery = '';
      this.searchOpen = false;
      this.$refs.searchInput && this.$refs.searchInput.focus();
    },
    searchEnter() {
      if (this.searchResults.length > 0) {
        this.pickSearchResult(this.searchResults[0]);
      }
    },
    pickSearchResult(item) {
      this.closeSearch();
      this.searchQuery = '';
      this.$store.commit('OPEN_TEST_RUNNER', item.id);
    },
    selectLocale(code) {
      this.currentLocale = code;
      const loc = this.locales.find(l => l.code === code);
      if (loc) this.$i18n.locale = loc.i18nCode;
      this.localeOpen = false;

      // Persist to the profile when the language maps to a backend-supported
      // locale (users.locale enum: en, es, th, ja, zh-cn).
      const backendLocale = { en: 'en', es: 'es', ja: 'ja', zh: 'zh-cn' }[loc && loc.i18nCode];
      if (backendLocale && this.$store.getters['auth/isLoggedIn']) {
        this.$http.put('/api/account/profile', { locale: backendLocale })
          .then(() => this.$store.dispatch('auth/fetchMe').catch(() => {}))
          .catch(() => {});
      }
    },
    orgInitials(name) {
      return (name || '?').split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase();
    },
    orgBadgeStyle(org) {
      const palette = [
        ['#bae6fd', '#003e6b'], ['#fde68a', '#78350f'], ['#bbf7d0', '#14532d'],
        ['#ddd6fe', '#4c1d95'], ['#fecaca', '#7f1d1d'],
      ];
      const [bg, fg] = palette[org.id % palette.length];
      return `background:${bg};color:${fg}`;
    },
    // Multi-org: switch the active organization without logging out. Permissions
    // are recomputed server-side for the selected org.
    async switchOrg(id) {
      if (id === this.currentOrganizationId || this.switchingOrgId !== null) {
        this.avatarOpen = false;
        this.orgMenuOpen = false;
        return;
      }
      this.switchingOrgId = id;
      try {
        await this.$store.dispatch('auth/switchOrganization', id);
        this.avatarOpen = false;
        this.orgMenuOpen = false;
        this.openSection = 'learn';
        const org = this.organizations.find(o => o.id === id);
        this.$store.dispatch('toast', org ? `Switched to ${org.name}` : 'Organization switched');
        if (this.$route.path !== '/dashboard') {
          this.$router.push('/dashboard').catch(() => {});
        }
      } catch (e) {
        this.$store.dispatch('toast', 'Could not switch organization');
      } finally {
        this.switchingOrgId = null;
      }
    },
    async logout() {
      this.avatarOpen = false;
      try {
        await this.$store.dispatch('auth/logout');
      } finally {
        this.$router.push('/login').catch(() => {});
      }
    },
    async stopImpersonating() {
      if (this.stoppingImpersonation) return;
      this.stoppingImpersonation = true;
      try {
        await this.$store.dispatch('auth/stopImpersonating');
        this.$router.push('/partner/org/users').catch(() => {});
      } catch (_e) {
        this.$store.dispatch('toast', 'Could not stop impersonating');
      } finally {
        this.stoppingImpersonation = false;
      }
    },
    // ── Organization impersonation (portal-app parity) ─────────────────────
    onOrgImpSearch() {
      if (this.orgImpSearchTimer) clearTimeout(this.orgImpSearchTimer);
      this.orgImpSearchTimer = setTimeout(() => this.loadOrgImpResults(), 250);
    },
    async loadOrgImpResults() {
      this.orgImpLoading = true;
      try {
        this.orgImpResults = await this.$store.dispatch(
          'auth/searchImpersonableOrganizations', this.orgImpQuery,
        );
      } catch (_e) {
        this.orgImpResults = [];
      } finally {
        this.orgImpLoading = false;
      }
    },
    async startOrgImpersonation(id) {
      if (this.startingOrgImpId !== null) return;
      this.startingOrgImpId = id;
      try {
        const data = await this.$store.dispatch('auth/startOrganizationImpersonation', id);
        this.orgMenuOpen = false;
        this.orgImpQuery = '';
        this.orgImpResults = [];
        const name = data && data.impersonated_organization && data.impersonated_organization.name;
        this.$store.dispatch('toast', this.$t('ui.orgImpersonation.started', { name: name || '' }));
        this.openSection = 'partner';
        if (this.$route.path !== '/dashboard') this.$router.push('/dashboard').catch(() => {});
      } catch (e) {
        const msg = (e && e.response && e.response.data && e.response.data.message)
          || this.$t('ui.orgImpersonation.startError');
        this.$store.dispatch('toast', msg);
      } finally {
        this.startingOrgImpId = null;
      }
    },
    async stopOrgImpersonation() {
      if (this.stoppingOrgImpersonation) return;
      this.stoppingOrgImpersonation = true;
      try {
        await this.$store.dispatch('auth/stopOrganizationImpersonation');
        this.$store.dispatch('toast', this.$t('ui.orgImpersonation.stopped'));
        if (this.$route.path !== '/dashboard') this.$router.push('/dashboard').catch(() => {});
      } catch (_e) {
        this.$store.dispatch('toast', this.$t('ui.orgImpersonation.stopError'));
      } finally {
        this.stoppingOrgImpersonation = false;
      }
    },
    goToAccount(path) {
      this.avatarOpen = false;
      this.$router.push(path).catch(() => {});
    },
    openTokenPurchase() {
      this.avatarOpen = false;
      this.$store.commit('OPEN_TOKEN_PURCHASE');
    },
    setTheme(t) {
      this.theme = applyTheme(t);
      this.persistTheme(t);
    },
    applyStoredAppearance() {
      const a = loadAppearance();
      this.theme = applyTheme(a.theme);
      applyFontSize(a.fontSize);
      applyColorblind(a.colorblind);
      applyAda(a.ada);
    },
    persistTheme(t) {
      // Best-effort sync to the backend; the local apply already happened.
      if (this.$store.getters['auth/isLoggedIn']) {
        this.$http.put('/api/account/preferences', { theme: t }).catch(() => {});
      }
    },
    syncLocaleFromUser() {
      const user = this.$store.getters['auth/user'] || {};
      if (!user.locale) return;
      const loc = this.locales.find(l => l.i18nCode === user.locale || l.code.toLowerCase().startsWith(user.locale.toLowerCase()));
      if (loc) {
        this.currentLocale = loc.code;
        this.$i18n.locale = loc.i18nCode;
      }
    },
    // Learn iframe changed language → keep topbar flag + i18n in sync.
    applyShellLocaleFromLearn(i18nCode) {
      const loc = this.locales.find(l => l.i18nCode === i18nCode);
      if (!loc) return;
      this.currentLocale = loc.code;
      if (this.$i18n.locale !== loc.i18nCode) {
        this.$i18n.locale = loc.i18nCode;
      }
    },

    // ── Enterprise section reorder ─────────────────────────
    // Per-item `perm` = { action, resource } gates an item individually within
    // an otherwise broadly-visible section. Items without `perm` stay visible.
    canSeeProductSection(section) {
      if (section.enterpriseOnly && !this.isEnterprise) return false;
      if (section.requires === 'content') return this.canSeeContent;
      if (section.requires === 'italentup') return this.$store.getters['auth/can']('italentup:Access', 'italentup:*:*');
      return true;
    },
    canSeeNavItem(item) {
      if (!item.perm) return true;
      return this.$store.getters['auth/can'](item.perm.action, item.perm.resource || '*:*:*');
    },
    // Flat, permission-filtered list of leaf items for a section, regardless of
    // whether the section is a flat array or grouped ({heading, items}) blocks.
    navItemsFor(key) {
      return flattenNavItems(ALL_NAV_ITEMS[key]).filter(item => this.canSeeNavItem(item));
    },
    // Grouped, permission-filtered blocks for a section. Flat sections collapse
    // into a single headingless group; empty groups (all items filtered) drop.
    navGroupsFor(key) {
      return normalizeNavGroups(ALL_NAV_ITEMS[key])
        .map(group => ({
          heading: group.heading,
          items: group.items.filter(item => this.canSeeNavItem(item)),
        }))
        .filter(group => group.items.length > 0);
    },
    isSectionActive(section) {
      return (section.activeRoutes || []).includes(this.$route.name) ||
             this.openSection === section.key;
    },
    openReorder() {
      this.reorderDraft = this.enterpriseOrder.slice();
      this.productDraft = this.productOrder.slice();
      this.reorderOpen = true;
    },
    closeReorder() {
      this.reorderOpen = false;
      this.reorderDragKey = null;
      this.reorderDragGroup = null;
    },
    saveReorder() {
      this.enterpriseOrder = this.reorderDraft.slice();
      this.productOrder = this.productDraft.slice();
      localStorage.setItem('mpts.enterprise.order', JSON.stringify(this.enterpriseOrder));
      localStorage.setItem('mpts.product.order.v2', JSON.stringify(this.productOrder));
      this.reorderOpen = false;
      this.reorderDragKey = null;
      this.reorderDragGroup = null;
      this.$store.dispatch && this.$store.dispatch('toast', this.$t('ui.reorder.saved'));
    },
    resetReorderDraft() {
      this.reorderDraft = DEFAULT_ENTERPRISE_ORDER.slice();
      this.productDraft = DEFAULT_PRODUCT_ORDER.slice();
    },
    // group: 'product' | 'enterprise' — selects which draft list to mutate.
    draftFor(group) { return group === 'product' ? 'productDraft' : 'reorderDraft'; },
    moveReorderItem(key, direction, group) {
      const prop = this.draftFor(group);
      const list = this[prop];
      const i = list.indexOf(key);
      const j = i + direction;
      if (i < 0 || j < 0 || j >= list.length) return;
      const next = list.slice();
      [next[i], next[j]] = [next[j], next[i]];
      this[prop] = next;
    },
    onReorderDragStart(e, key, group) {
      this.reorderDragKey = key;
      this.reorderDragGroup = group;
      if (e.dataTransfer) {
        e.dataTransfer.effectAllowed = 'move';
        // Required by Firefox to initiate drag
        e.dataTransfer.setData('text/plain', key);
      }
    },
    onReorderDragOver(e, _targetKey, group) {
      // Only allow dropping within the same group; keep the two lists separate.
      if (this.reorderDragKey === null || this.reorderDragGroup !== group) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
    },
    onReorderDrop(e, targetKey, group) {
      e.preventDefault();
      const source = this.reorderDragKey;
      const sourceGroup = this.reorderDragGroup;
      this.reorderDragKey = null;
      this.reorderDragGroup = null;
      if (!source || sourceGroup !== group || source === targetKey) return;
      const prop = this.draftFor(group);
      const next = this[prop].slice();
      const fromIdx = next.indexOf(source);
      const toIdx   = next.indexOf(targetKey);
      if (fromIdx < 0 || toIdx < 0) return;
      next.splice(fromIdx, 1);
      next.splice(toIdx, 0, source);
      this[prop] = next;
    },
    onReorderDragEnd() {
      this.reorderDragKey = null;
      this.reorderDragGroup = null;
    },
  },
};
</script>

<style>
.org-switch-spin { animation: org-switch-spin 0.7s linear infinite; }
@keyframes org-switch-spin { to { transform: rotate(360deg); } }
/* =========================================================
   DESIGN TOKENS
   ========================================================= */
:root {
  --brand-navy:      #0d1f3c;
  --brand-navy-2:    #0a1830;
  --brand-navy-hover:#152a4d;
  --brand-navy-soft: rgba(255,255,255,0.06);
  --brand-navy-text: #e8ecf3;
  --brand-navy-muted:#8a96ab;

  --accent:          #0080c9;
  --accent-dark:     #006aaa;
  --accent-light:    #bae6fd;
  --accent-soft:     #e0f2fe;

  --primary:         #005b99;
  --primary-2:       #0080c9;
  --primary-soft:    #e0f2fe;

  --bg:              #f5f6f8;
  --bg-alt:          #fafbfc;
  --card:            #ffffff;
  --card-hover:      #fafbfc;
  --border:          #e5e7eb;
  --border-strong:   #d1d5db;
  --border-soft:     #eef0f3;

  --ink:             #0f172a;
  --ink-2:           #1e293b;
  --ink-3:           #475569;
  --muted:           #64748b;
  --muted-2:         #94a3b8;
  --muted-3:         #cbd5e1;

  --success:         #16a34a;
  --success-soft:    #f0fdf4;
  --warning:         #f59e0b;
  --warning-soft:    #fffbeb;
  --danger:          #dc2626;
  --danger-soft:     #fef2f2;
  --info:            #0ea5e9;
  --info-soft:       #f0f9ff;

  --sidebar-w:       218px;
  --sidebar-w-col:   64px;
  --topbar-h:        56px;
  --radius:          10px;
  --radius-sm:       6px;
  --radius-lg:       14px;

  --shadow-sm:  0 1px 2px rgba(15,23,42,0.04);
  --shadow:     0 1px 3px rgba(15,23,42,0.05), 0 1px 2px rgba(15,23,42,0.03);
  --shadow-md:  0 4px 12px rgba(15,23,42,0.06), 0 2px 4px rgba(15,23,42,0.04);
  --shadow-lg:  0 12px 32px rgba(15,23,42,0.10), 0 4px 8px rgba(15,23,42,0.05);
  --shadow-pop: 0 20px 48px rgba(15,23,42,0.18);

  --font-sans: 'Nunito Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  --font-mono: 'JetBrains Mono', 'SF Mono', Consolas, monospace;
}

/* =========================================================
   DARK THEME
   ========================================================= */
[data-theme="dark"] {
  --bg:              #0f172a;
  --bg-alt:          #1a2540;
  --card:            #1e2d45;
  --card-hover:      #243350;
  --border:          #2d3f5a;
  --border-strong:   #3d5070;
  --border-soft:     #253450;
  --ink:             #e8edf5;
  --ink-2:           #d4dbe8;
  --ink-3:           #8fa3bf;
  --muted:           #5f7a9a;
  --muted-2:         #47607d;
  --muted-3:         #30455e;
  --accent-soft:     rgba(0,128,201,0.18);
  --primary-soft:    rgba(0,128,201,0.14);
  --success-soft:    rgba(22,163,74,0.15);
  --warning-soft:    rgba(245,158,11,0.15);
  --danger-soft:     rgba(220,38,38,0.15);
  --info-soft:       rgba(14,165,233,0.15);
  --shadow-sm:  0 1px 2px rgba(0,0,0,0.4);
  --shadow:     0 1px 3px rgba(0,0,0,0.5), 0 1px 2px rgba(0,0,0,0.35);
  --shadow-md:  0 4px 12px rgba(0,0,0,0.45), 0 2px 4px rgba(0,0,0,0.35);
  --shadow-lg:  0 12px 32px rgba(0,0,0,0.55), 0 4px 8px rgba(0,0,0,0.4);
  --shadow-pop: 0 20px 48px rgba(0,0,0,0.7);
}

/* =========================================================
   HIGH CONTRAST THEME (accessibility)
   ========================================================= */
[data-theme="high_contrast"] {
  --bg:              #000000;
  --bg-alt:          #000000;
  --card:            #0a0a0a;
  --card-hover:      #141414;
  --border:          #ffffff;
  --border-strong:   #ffffff;
  --border-soft:     #cccccc;
  --ink:             #ffffff;
  --ink-2:           #ffffff;
  --ink-3:           #ffff00;
  --muted:           #ffffff;
  --muted-2:         #dddddd;
  --muted-3:         #aaaaaa;
  --accent:          #ffff00;
  --accent-dark:     #ffff00;
  --accent-light:    #ffff66;
  --accent-soft:     #333300;
  --primary:         #ffff00;
  --primary-2:       #ffff00;
  --primary-soft:    #333300;
  --success:         #00ff00;
  --success-soft:    #003300;
  --warning:         #ffaa00;
  --warning-soft:    #332200;
  --danger:          #ff5555;
  --danger-soft:     #330000;
  --info:            #55ccff;
  --info-soft:       #002233;
  --brand-navy:      #000000;
  --brand-navy-text: #ffffff;
}

/* =========================================================
   ATHENA THEME (brand variant — warm slate + gold)
   ========================================================= */
[data-theme="athena"] {
  --bg:              #f4f1ea;
  --bg-alt:          #faf8f3;
  --card:            #ffffff;
  --card-hover:      #faf7f0;
  --border:          #e2dccf;
  --border-strong:   #cfc6b2;
  --border-soft:     #ece7db;
  --ink:             #2a2620;
  --ink-2:           #3d382f;
  --ink-3:           #6a6252;
  --muted:           #8a8172;
  --muted-2:         #a89f8d;
  --muted-3:         #c9c0ad;
  --accent:          #b8860b;
  --accent-dark:     #946a08;
  --accent-light:    #f0e2b8;
  --accent-soft:     #f6efd9;
  --primary:         #6b4f9e;
  --primary-2:       #8267b8;
  --primary-soft:    #efe9f7;
  --brand-navy:      #3d2f5a;
  --brand-navy-text: #f0ebf7;
}

/* =========================================================
   FONT SIZE (accessibility)
   The app uses fixed px sizing throughout, so scaling only the base
   font barely moves the needle. `zoom` on the app root scales the whole
   UI proportionally (text + spacing) — which is exactly what an
   accessibility text-size control should do — and, unlike transform,
   keeps layout and position:fixed math consistent.
   ========================================================= */
[data-fontsize="small"]   body { zoom: 0.9; }
[data-fontsize="medium"]  body { zoom: 1; }
[data-fontsize="large"]   body { zoom: 1.15; }
[data-fontsize="x-large"] body { zoom: 1.3; }

/* Firefox <126 ignores `zoom`; fall back to nudging the base font. */
@supports not (zoom: 1) {
  [data-fontsize="small"]   body { font-size: 12.5px; }
  [data-fontsize="large"]   body { font-size: 16px; }
  [data-fontsize="x-large"] body { font-size: 18px; }
}

/* =========================================================
   COLORBLIND-SAFE MODE (Okabe–Ito palette for status colors)
   Swaps the semantic palette app-wide (charts, pass/fail, badges…)
   and adds a non-color cue: status pills get a visible outline so they
   are distinguishable without relying on hue alone.
   ========================================================= */
.cb-mode {
  --success:      #009e73;
  --warning:      #e69f00;
  --danger:       #d55e00;
  --info:         #0072b2;
  --success-soft: rgba(0,158,115,0.16);
  --warning-soft: rgba(230,159,0,0.16);
  --danger-soft:  rgba(213,94,0,0.16);
  --info-soft:    rgba(0,114,178,0.16);
  --accent:       #0072b2;
  --accent-dark:  #005b8f;
}
.cb-mode .badge,
.cb-mode .pill,
.cb-mode .tag,
.cb-mode .status,
.cb-mode [class*="badge"],
.cb-mode [class*="pill"],
.cb-mode [class*="chip"] {
  outline: 1.5px solid currentColor;
  outline-offset: -1.5px;
  font-weight: 700 !important;
}

/* =========================================================
   ADA SUPPORT (larger hit areas, readable text, strong focus)
   ========================================================= */
.ada-mode {
  --radius: 6px;
}
/* More readable body copy: extra line-height + letter-spacing + weight. */
.ada-mode body,
.ada-mode .content,
.ada-mode p,
.ada-mode li,
.ada-mode label,
.ada-mode .form-label,
.ada-mode td,
.ada-mode th {
  line-height: 1.75 !important;
  letter-spacing: 0.012em;
}
/* Links are underlined so they don't depend on color alone. */
.ada-mode a:not(.btn-primary):not(.btn-ghost):not([class*="nav"]) {
  text-decoration: underline;
  text-underline-offset: 2px;
}
/* Always-visible, high-contrast focus ring for keyboard + pointer. */
.ada-mode a:focus,
.ada-mode button:focus,
.ada-mode input:focus,
.ada-mode select:focus,
.ada-mode textarea:focus,
.ada-mode [tabindex]:focus,
.ada-mode a:focus-visible,
.ada-mode button:focus-visible,
.ada-mode input:focus-visible,
.ada-mode select:focus-visible,
.ada-mode textarea:focus-visible,
.ada-mode [tabindex]:focus-visible {
  outline: 3px solid var(--accent) !important;
  outline-offset: 2px !important;
  box-shadow: 0 0 0 4px var(--accent-soft) !important;
}
/* Bigger tap targets on interactive controls. */
.ada-mode button,
.ada-mode .btn-primary,
.ada-mode .btn-ghost,
.ada-mode .form-input,
.ada-mode select {
  min-height: 40px;
}

/* =========================================================
   RESET / BASE
   ========================================================= */
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
html, body { height: 100%; }
body {
  font-family: var(--font-sans);
  font-size: 14px;
  color: var(--ink);
  background: var(--bg);
  line-height: 1.5;
  -webkit-font-smoothing: antialiased;
}
button { font: inherit; cursor: pointer; border: none; background: none; color: inherit; }
a { color: inherit; text-decoration: none; }
input, select, textarea { font: inherit; }
ul { list-style: none; }
svg { display: block; }

/* =========================================================
   APP LAYOUT
   ========================================================= */
.app {
  display: grid;
  grid-template-columns: var(--sidebar-w) 1fr;
  min-height: 100vh;
  /* Clip horizontal overflow at the layout root rather than at html/body
     (the latter breaks `position: fixed` on iOS Safari). */
  overflow-x: clip;
  max-width: 100vw;
}
.app.collapsed { grid-template-columns: var(--sidebar-w-col) 1fr; }

/* =========================================================
   SIDEBAR
   ========================================================= */
.sidebar {
  background: var(--brand-navy);
  color: var(--brand-navy-text);
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 0;
  align-self: start;
  height: 100vh;
  height: 100dvh;
  max-height: 100vh;
  max-height: 100dvh;
  overflow: hidden;
  border-right: 1px solid rgba(0,0,0,0.1);
  /* position:sticky makes the sidebar its own stacking context; lift it above
     the main area (and topbar z=30 / dropdowns z=60) so the hover-flyout
     submenu paints over page content, while staying below modals (z=200+). */
  z-index: 70;
}
/* Only the middle nav region scrolls; header + footer stay pinned so the
   Collapse / Reorder controls are always reachable without scrolling. */
.sidebar-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
}
.sidebar-scroll::-webkit-scrollbar { width: 4px; }
.sidebar-scroll::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.12); border-radius: 2px; }
.sidebar-header {
  height: var(--topbar-h);
  padding: 0 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid rgba(255,255,255,0.06);
  flex-shrink: 0;
}

/* Logo */
.logo {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  min-width: 0;
}
.logo-img {
  height: 44px;
  width: auto;
  max-width: 180px;
  object-fit: contain;
}
.logo-icon {
  width: 34px;
  height: 34px;
  object-fit: contain;
  border-radius: 6px;
  flex-shrink: 0;
}
.logo-text {
  font-weight: 800;
  font-size: 19px;
  letter-spacing: -0.02em;
  color: #fff;
  white-space: nowrap;
}
.logo-mark {
  width: 26px; height: 26px;
  background: var(--accent);
  border-radius: 7px;
  display: grid; place-items: center;
  box-shadow: 0 2px 6px rgba(0,128,201,0.4);
  flex-shrink: 0;
}
.logo-mark::before {
  content: '';
  width: 12px; height: 12px;
  border: 2px solid var(--brand-navy);
  border-radius: 2px;
  border-bottom: 0;
  border-right: 0;
}

.sidebar-section-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--brand-navy-muted);
  padding: 16px 20px 8px;
  flex-shrink: 0;
}
.app.collapsed .sidebar-section-label { display: none; }

.nav-group { padding: 4px 10px; }
.nav-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 9px 12px;
  border-radius: 8px;
  cursor: pointer;
  color: var(--brand-navy-text);
  font-size: 13.5px;
  font-weight: 500;
  margin-bottom: 2px;
  transition: all 0.15s ease;
  position: relative;
  white-space: nowrap;
}
.nav-item:hover { background: var(--brand-navy-hover); color: #fff; }
.nav-item.active {
  background: var(--accent);
  color: #fff;
  font-weight: 700;
  box-shadow: 0 2px 8px rgba(0,128,201,0.25);
}
.nav-item.active .nav-icon { color: #fff; }
.nav-item.secondary.active {
  background: rgba(0,128,201,0.18);
  color: var(--accent);
}
.nav-icon {
  width: 18px; height: 18px;
  flex-shrink: 0;
  stroke-width: 1.8;
  color: var(--brand-navy-muted);
}
.nav-item:hover .nav-icon { color: #fff; }
.nav-item.active .nav-icon { color: #fff; }
.nav-item.secondary.active .nav-icon { color: var(--accent); }
.nav-chevron { margin-left: auto; opacity: 0.5; flex-shrink: 0; }
.app.collapsed .nav-label,
.app.collapsed .nav-chevron { display: none; }
.app.collapsed .nav-item { justify-content: center; padding: 10px; }

/* Submenus render as hover-flyout panels (HubSpot style) on desktop; the
   ≤ 1024 px drawer overrides these to an accordion (see responsive block). */
.nav-subitems {
  display: none;
}

/* Sub-item row: link + star button.
   Rows share vertical edges (no margin gap) so the pointer never drops into a
   non-hover void while traversing the list. */
.nav-subitem-wrap {
  display: flex;
  align-items: center;
  border-radius: 6px;
  margin-bottom: 0;
  transition: background 0.12s;
}
.nav-subitem-wrap:hover { background: var(--brand-navy-hover); }
.nav-subitem-wrap:hover .fav-toggle { opacity: 1; }

.nav-subitem {
  flex: 1;
  display: block;
  padding: 8px 12px;
  font-size: 13px;
  color: var(--brand-navy-muted);
  border-radius: 6px;
  cursor: pointer;
  transition: color 0.12s;
}
.nav-subitem-wrap:hover .nav-subitem { color: #fff; }
.nav-subitem.active { color: var(--accent); font-weight: 600; }

.fav-toggle {
  padding: 6px 8px;
  color: var(--brand-navy-muted);
  border-radius: 4px;
  opacity: 0;
  transition: opacity 0.12s, color 0.12s;
  flex-shrink: 0;
  cursor: pointer;
}
.fav-toggle:hover { color: var(--warning); }
.fav-toggle.active { opacity: 1 !important; }

/* Favorites section items */
.fav-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  font-size: 13px;
  color: var(--brand-navy-muted);
  border-radius: 6px;
  cursor: pointer;
  margin-bottom: 1px;
  transition: all 0.12s;
}
.fav-item:hover { color: #fff; background: var(--brand-navy-hover); }
.fav-item.active { color: var(--accent); font-weight: 600; }

/* Flyout title — visible inside the hover-flyout popover */
.nav-flyout-title { display: none; }

/* Sidebar sub-items become a hover-flyout popover (HubSpot style).
   `top` is set inline per-section (flyoutStyle) so the panel aligns with the
   hovered item; `left` sits flush against the sidebar edge (viewport coords,
   because the panel is position:fixed to escape the sidebar's overflow clip). */
.nav-section { position: relative; }

.nav-section:hover > .nav-subitems {
  display: block;
  position: fixed;
  left: var(--sidebar-w);
  min-width: 220px;
  padding: 8px;
  background: var(--brand-navy);
  border: 1px solid rgba(255,255,255,0.10);
  border-radius: 10px;
  box-shadow: 0 14px 36px rgba(0,0,0,0.45);
  z-index: 200;
  overflow: visible;
  animation: navFlyoutIn 0.12s ease;
}
.app.collapsed .nav-section:hover > .nav-subitems {
  left: var(--sidebar-w-col);
}
@keyframes navFlyoutIn {
  from { opacity: 0; transform: translateX(-4px); }
  to   { opacity: 1; transform: translateX(0); }
}

/* Invisible bridge so hover doesn't break across the gap between the sidebar
   edge and the level-1 flyout. Wider + vertical overhang for tolerance. */
.nav-section:hover > .nav-subitems::before {
  content: '';
  position: absolute;
  left: -16px;
  top: -12px;
  bottom: -12px;
  width: 16px;
}

.nav-section .nav-flyout-title {
  display: block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--brand-navy-muted);
  padding: 4px 10px 8px;
  border-bottom: 1px solid rgba(255,255,255,0.08);
  margin-bottom: 6px;
  white-space: nowrap;
}

/* Intent group heading inside a grouped flyout (Fase 2). Non-clickable. */
.nav-section .nav-flyout-group-label {
  display: block;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--brand-navy-muted);
  padding: 10px 10px 4px;
  white-space: nowrap;
  opacity: 0.85;
}
.nav-section .nav-flyout-group-label:first-of-type { padding-top: 2px; }

.nav-section .nav-subitem {
  white-space: nowrap;
  padding: 7px 10px;
}

/* ── Cascade (level-2) menu — HubSpot-style multi-level flyout ──────
   A group with a single leaf renders as a direct link row (.is-leaf).
   A group with several leaves renders as a hoverable row (.has-children)
   whose .nav-subpanel cascades out to the right on hover. The level-1
   panel is overflow:visible so the level-2 panel escapes it. */
.nav-section .nav-flyout-group {
  position: relative;
  border-radius: 6px;
}
/* Group header row (multi-leaf): label + right chevron. Full-width, generous
   vertical padding so the entire row is an easy hover target. */
.nav-flyout-group-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 12px;
  font-size: 13px;
  color: var(--brand-navy-muted);
  border-radius: 6px;
  cursor: default;
  white-space: nowrap;
  transition: color 0.12s, background 0.12s;
}
.nav-flyout-group.has-children:hover > .nav-flyout-group-row,
.nav-flyout-group.has-children.is-open > .nav-flyout-group-row {
  color: #fff;
  background: var(--brand-navy-hover);
}
.nav-flyout-group-title { font-weight: 600; }
.nav-flyout-group-chevron { flex-shrink: 0; opacity: 0.7; }

/* Level-2 subpanel: cascades to the right of its group row. It opens on CSS
   :hover AND while the JS hover-intent state marks this group `.is-open`,
   which keeps it open long enough to traverse the diagonal gap. */
.nav-flyout-group .nav-subpanel {
  display: none;
  position: absolute;
  left: 100%;
  top: 0;
  min-width: 210px;
  max-height: calc(100vh - 24px);
  overflow-y: auto;
  overflow-x: hidden;
  padding: 8px;
  margin-left: 6px;
  background: var(--brand-navy);
  border: 1px solid rgba(255,255,255,0.10);
  border-radius: 10px;
  box-shadow: 0 14px 36px rgba(0,0,0,0.45);
  z-index: 210;
  animation: navFlyoutIn 0.12s ease;
}
.nav-flyout-group.has-children:hover > .nav-subpanel,
.nav-flyout-group.has-children.is-open > .nav-subpanel {
  display: block;
}
/* Invisible hover bridge spanning the full gap between the level-1 row and its
   level-2 subpanel. Anchored on the group (overflow:visible) rather than the
   subpanel (which clips via overflow) so it is never cut off. It starts just
   inside the group's right edge and runs past the margin gap, across the full
   row height plus vertical overhang, so a diagonal pointer path never crosses
   a hover void. Kept visible while hovering OR while JS marks it open. */
.nav-flyout-group.has-children:hover::after,
.nav-flyout-group.has-children.is-open::after {
  content: '';
  position: absolute;
  left: 100%;
  top: -14px;
  bottom: -14px;
  width: 30px;
  z-index: 209;
}

.sidebar-footer {
  border-top: 1px solid rgba(255,255,255,0.06);
  padding: 10px 16px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.collapse-btn, .sidebar-footer-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: var(--brand-navy-muted);
  cursor: pointer;
  padding: 6px 8px;
  border-radius: 6px;
  transition: all 0.15s;
  background: none;
  border: none;
  font-family: inherit;
  text-align: left;
  width: 100%;
}
.collapse-btn:hover, .sidebar-footer-btn:hover { color: #fff; background: var(--brand-navy-hover); }
.sidebar-footer-btn:focus-visible { outline: 2px solid var(--accent); outline-offset: 1px; }
.app.collapsed .collapse-btn-label,
.app.collapsed .sidebar-footer-btn-label { display: none; }
.app.collapsed .sidebar-footer-btn { justify-content: center; }
.app.collapsed .collapse-btn svg { transform: rotate(180deg); }

/* =========================================================
   TOPBAR
   ========================================================= */
.main-area { display: flex; flex-direction: column; min-width: 0; }
.impersonation-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 8px 24px;
  background: var(--warning-soft);
  color: var(--ink-2);
  border-bottom: 1px solid var(--warning);
  font-size: 13px;
  position: sticky;
  top: 0;
  z-index: 40;
}
.impersonation-banner b { color: var(--ink); }
.topbar {
  height: var(--topbar-h);
  background: var(--card);
  border-bottom: 1px solid var(--border);
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 0 24px;
  position: sticky;
  top: 0;
  z-index: 30;
}
.breadcrumbs {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: var(--muted);
}
.breadcrumb-sep { color: var(--muted-3); }
.breadcrumb-item { cursor: pointer; }
.breadcrumb-item:hover { color: var(--ink-2); }
.breadcrumb-item.active {
  color: var(--ink);
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.breadcrumb-dot {
  width: 6px; height: 6px; border-radius: 50%;
  background: var(--accent);
  display: inline-block;
}
.topbar-search {
  flex: 1;
  max-width: 520px;
  margin: 0 auto;
  position: relative;
}
.topbar-search input {
  width: 100%;
  height: 36px;
  border: 1px solid var(--border);
  background: var(--bg-alt);
  border-radius: 8px;
  padding: 0 44px 0 38px;
  color: var(--ink);
  font-size: 13.5px;
  transition: all 0.15s;
}
.topbar-search input:focus {
  outline: none;
  border-color: var(--accent);
  background: var(--card);
  box-shadow: 0 0 0 3px rgba(0,128,201,0.1);
}
.topbar-search input::placeholder { color: var(--muted-2); }
.topbar-search-icon {
  position: absolute; left: 12px; top: 50%; transform: translateY(-50%);
  color: var(--muted-2);
  pointer-events: none;
}
.topbar-search-kbd {
  position: absolute; right: 8px; top: 50%; transform: translateY(-50%);
  display: flex; align-items: center; gap: 3px;
  padding: 2px 7px;
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 4px;
  font-family: var(--font-mono);
  font-size: 10.5px;
  color: var(--muted);
  font-weight: 500;
}
.topbar-search-clear {
  position: absolute; right: 10px; top: 50%; transform: translateY(-50%);
  width: 20px; height: 20px;
  display: grid; place-items: center;
  border-radius: 4px;
  color: var(--muted);
  cursor: pointer;
}
.topbar-search-clear:hover { color: var(--ink); background: var(--bg); }

/* Search dropdown */
.search-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  left: 0; right: 0;
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: var(--shadow-lg);
  z-index: 50;
  overflow: hidden;
  animation: searchDropIn 0.12s ease;
}
@keyframes searchDropIn {
  from { opacity: 0; transform: translateY(-4px); }
  to   { opacity: 1; transform: translateY(0); }
}
.search-section-label {
  padding: 8px 14px 4px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--muted);
}
.search-result {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  cursor: pointer;
  transition: background 0.12s;
}
.search-result:hover { background: var(--bg); }
.search-result-cover {
  width: 44px; height: 44px;
  border-radius: 8px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.search-result-logo {
  max-width: 28px;
  max-height: 20px;
  object-fit: contain;
  filter: brightness(0) invert(1);
}
.search-result-body { flex: 1; min-width: 0; }
.search-result-title {
  font-size: 13.5px;
  font-weight: 600;
  color: var(--ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.search-result-meta { font-size: 11.5px; color: var(--muted); margin-top: 1px; }
.search-empty {
  padding: 16px 14px;
  font-size: 13px;
  color: var(--muted);
  text-align: center;
}
.search-empty strong { color: var(--ink); }

.topbar-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}
.topbar-group {
  display: flex;
  align-items: center;
  gap: 6px;
  position: relative;
}

/* ── Active organization switcher (topbar) ─────────────────────────────── */
.org-switch { position: relative; }
.org-switch-btn {
  display: flex; align-items: center; gap: 9px;
  padding: 5px 10px 5px 6px; border: 1px solid var(--border); border-radius: 10px;
  background: var(--card, #fff); cursor: pointer; transition: border-color 0.15s, background 0.15s;
  max-width: 240px;
}
.org-switch-btn:hover { border-color: var(--accent, #38bdf8); background: var(--bg-alt, #fafbfc); }
.org-switch-btn.locked { cursor: default; }
.org-switch-btn.locked:hover { border-color: var(--border); background: var(--card, #fff); }
.org-switch-badge {
  width: 30px; height: 30px; border-radius: 8px; flex-shrink: 0;
  display: grid; place-items: center; font-size: 11.5px; font-weight: 800;
}
.org-switch-badge.sm { width: 28px; height: 28px; border-radius: 7px; }
.org-switch-text { display: flex; flex-direction: column; align-items: flex-start; min-width: 0; }
.org-switch-label {
  font-size: 9.5px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em;
  color: var(--muted); line-height: 1.1;
}
.org-switch-name {
  font-size: 13px; font-weight: 700; color: var(--ink); line-height: 1.2;
  max-width: 150px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.org-switch-dropdown {
  position: absolute; top: calc(100% + 8px); left: 0; z-index: 200;
  min-width: 260px; background: var(--card, #fff);
  border: 1px solid var(--border); border-radius: 12px;
  box-shadow: 0 16px 40px rgba(15,23,42,0.18); padding: 8px;
}
.org-switch-dropdown-label {
  font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em;
  color: var(--muted); padding: 6px 10px 8px;
}
.org-imp-dropdown { top: calc(100% + 8px); left: 0; margin-top: 2px; }
.org-imp-label { border-top: 1px solid var(--border); margin-top: 6px; padding-top: 10px; }
.org-imp-search {
  width: 100%; box-sizing: border-box; border: 1px solid var(--border);
  border-radius: 8px; padding: 7px 10px; font-size: 12.5px; margin-bottom: 6px;
  background: var(--card, #fff); color: var(--ink); outline: none;
}
.org-imp-search:focus { border-color: var(--accent); }
.org-imp-hint { font-size: 12px; color: var(--muted); padding: 6px 10px; }
.org-imp-list { max-height: 240px; overflow-y: auto; }
.org-switch-option {
  display: flex; align-items: center; gap: 10px;
  padding: 8px 10px; border-radius: 9px; cursor: pointer; transition: background 0.12s;
}
.org-switch-option:hover { background: var(--bg-alt, #fafbfc); }
.org-switch-option.active { background: var(--accent-soft, #e0f2fe); }
.org-switch-option-info { min-width: 0; }
.org-switch-option-name { font-size: 13px; font-weight: 700; color: var(--ink); }
.org-switch-option-role { font-size: 11.5px; color: var(--muted); margin-top: 1px; }
/* Subtle 1px divider between adjacent groups, sitting in the middle of the 16px outer gap */
.topbar-group + .topbar-group::before {
  content: "";
  position: absolute;
  left: -9px;
  top: 8px;
  bottom: 8px;
  width: 1px;
  background: var(--border);
}
.locale {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 8px;
  border-radius: 8px;
  cursor: pointer;
  position: relative;
  user-select: none;
}
.locale:hover { background: var(--bg); }
.locale-flag { font-size: 18px; line-height: 1; }
.locale-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: var(--shadow-lg);
  z-index: 60;
  min-width: 190px;
  overflow: hidden;
  animation: searchDropIn 0.12s ease;
}
.locale-option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 14px;
  cursor: pointer;
  font-size: 13px;
  color: var(--ink);
  transition: background 0.12s;
}
.locale-option:hover { background: var(--bg); }
.locale-option.active { background: var(--primary-soft); color: var(--primary-2); font-weight: 600; }
.locale-flag-img { width: 22px; height: 15px; object-fit: cover; border-radius: 2px; flex-shrink: 0; }
.locale-option-flag-img { width: 20px; height: 14px; object-fit: cover; border-radius: 2px; flex-shrink: 0; }
.locale-option-label { flex: 1; }
.avatar-account-section-label {
  padding: 7px 16px 4px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: var(--muted);
}
.avatar-account-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  cursor: pointer;
  transition: background 0.12s;
}
.avatar-account-row:hover { background: var(--bg); }
.avatar-account-row.active { background: var(--primary-soft); }
.avatar-account-icon {
  width: 32px; height: 32px;
  border-radius: 8px;
  display: grid; place-items: center;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
}
.avatar-account-info { flex: 1; min-width: 0; }
.avatar-account-name { font-size: 12.5px; font-weight: 600; color: var(--ink); }
.avatar-account-type { font-size: 11px; color: var(--muted); margin-top: 1px; }
.topbar-icon {
  width: 36px; height: 36px;
  display: grid; place-items: center;
  border-radius: 8px;
  color: var(--muted);
  position: relative;
  transition: all 0.15s;
}
.topbar-icon:hover { background: var(--bg); color: var(--ink); }
.topbar-icon .badge {
  position: absolute; top: 6px; right: 7px;
  width: 8px; height: 8px;
  background: var(--danger);
  border-radius: 50%;
  border: 2px solid var(--card);
}
.avatar-wrap { position: relative; }
.avatar {
  width: 34px; height: 34px;
  border-radius: 50%;
  background: linear-gradient(135deg, #bae6fd 0%, #0080c9 100%);
  color: #003e6b;
  display: grid; place-items: center;
  font-weight: 700;
  font-size: 13px;
  cursor: pointer;
  border: 2px solid transparent;
  transition: all 0.15s;
  flex-shrink: 0;
}
.avatar { overflow: hidden; }
.avatar:hover { border-color: var(--accent-light); }
.avatar-img { width: 100%; height: 100%; object-fit: cover; border-radius: 50%; display: block; }
.avatar-menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 12px;
  box-shadow: var(--shadow-lg);
  z-index: 60;
  width: 228px;
  overflow: hidden;
  animation: searchDropIn 0.12s ease;
}
.avatar-menu-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: var(--bg-alt);
  border-bottom: 1px solid var(--border-soft);
}
.avatar-menu-avatar {
  width: 36px; height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #bae6fd 0%, #0080c9 100%);
  color: #003e6b;
  display: grid; place-items: center;
  font-weight: 700; font-size: 13px;
  flex-shrink: 0;
  overflow: hidden;
}
.avatar-menu-name { font-size: 13px; font-weight: 700; color: var(--ink); }
.avatar-menu-email { font-size: 11px; color: var(--muted); margin-top: 2px; }
.avatar-menu-divider { height: 1px; background: var(--border-soft); margin: 3px 0; }
.avatar-menu-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 9px 16px;
  font-size: 13px;
  color: var(--ink-2);
  cursor: pointer;
  transition: background 0.12s;
  text-align: left;
  border: none;
  background: none;
  font-family: inherit;
}
.avatar-menu-item:hover { background: var(--bg); }
.avatar-menu-item svg { color: var(--muted); flex-shrink: 0; }
.avatar-menu-item.danger { color: var(--danger); }
.avatar-menu-item.danger:hover { background: var(--danger-soft); }
.avatar-menu-item.danger svg { color: var(--danger); }

/* Theme toggle — stacked so the 2-button bar gets the full menu width and never clips */
.avatar-theme-row {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px 16px;
}
.avatar-theme-label {
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: var(--muted);
}
.avatar-theme-toggle {
  display: flex;
  gap: 2px;
  background: var(--bg);
  border-radius: 7px;
  padding: 2px;
  border: 1px solid var(--border);
  width: 100%;
}
.avatar-theme-opt {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 6px 8px;
  border-radius: 5px;
  font-size: 12px;
  font-weight: 500;
  color: var(--muted);
  transition: all 0.13s;
}
.avatar-theme-opt:hover { color: var(--ink); background: var(--card); }
.avatar-theme-opt.active { background: var(--card); color: var(--accent); box-shadow: var(--shadow-sm); }

/* Tokens */
.avatar-tokens-row {
  padding: 10px 16px 8px;
}
.avatar-tokens-header {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-bottom: 7px;
}
.avatar-tokens-label {
  font-size: 13px;
  color: var(--ink-3);
  flex: 1;
  font-weight: 500;
}
.avatar-tokens-count {
  font-size: 11px;
  font-weight: 700;
  color: var(--ink);
  font-family: var(--font-mono);
}
.avatar-tokens-track {
  height: 6px;
  background: var(--border);
  border-radius: 99px;
  overflow: hidden;
}
.avatar-tokens-fill {
  height: 100%;
  border-radius: 99px;
  transition: width 0.4s ease;
}
.avatar-tokens-meta {
  font-size: 10.5px;
  color: var(--muted);
  margin-top: 5px;
}
.avatar-tokens-count.low { color: var(--danger); }
.avatar-tokens-count b { font-weight: 700; }
.avatar-tokens-buy {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
  width: 100%;
  padding: 7px 10px;
  background: var(--accent);
  color: #fff;
  border: none;
  border-radius: 7px;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.13s, transform 0.13s;
  justify-content: center;
}
.avatar-tokens-buy:hover { background: var(--accent-dark); transform: translateY(-1px); }

/* =========================================================
   CONTENT
   ========================================================= */
.content {
  padding: 28px 32px 48px;
  flex: 1;
  min-width: 0;
  animation: pageIn 0.25s ease;
}
@keyframes pageIn {
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* =========================================================
   BUTTONS
   ========================================================= */
.btn-primary {
  background: var(--accent);
  color: #fff;
  font-weight: 700;
  font-size: 13px;
  padding: 8px 16px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.15s;
  box-shadow: 0 2px 4px rgba(0,128,201,0.2);
  white-space: nowrap;
}
.btn-primary:hover {
  background: var(--accent-dark);
  box-shadow: 0 4px 12px rgba(0,128,201,0.3);
  transform: translateY(-1px);
}
.btn-primary.sm { padding: 6px 12px; font-size: 12.5px; }

.btn-ghost {
  padding: 7px 14px;
  border: 1px solid var(--border);
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: var(--ink-3);
  background: var(--card);
  transition: all 0.15s;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.btn-ghost:hover { border-color: var(--border-strong); color: var(--ink); background: var(--card-hover); }

.btn-text {
  color: var(--primary-2);
  font-size: 13px;
  font-weight: 600;
  padding: 6px 8px;
  border-radius: 6px;
  cursor: pointer;
}
.btn-text:hover { background: var(--primary-soft); }

/* =========================================================
   GENERIC CARD
   ========================================================= */
.card {
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--shadow-sm);
}
.card-header {
  padding: 18px 22px;
  border-bottom: 1px solid var(--border-soft);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}
.card-title {
  font-size: 16px;
  font-weight: 700;
  color: var(--ink);
  letter-spacing: -0.01em;
}
.card-body { padding: 22px; }
.card-body.tight { padding: 16px 22px; }

/* =========================================================
   TABS
   ========================================================= */
.tabs {
  display: flex;
  gap: 24px;
  border-bottom: 1px solid var(--border);
  padding: 0 22px;
  margin: 0 -22px;
}
.tab {
  padding: 12px 2px;
  font-size: 13.5px;
  font-weight: 600;
  color: var(--muted);
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  transition: all 0.15s;
  display: flex;
  align-items: center;
  gap: 6px;
}
.tab:hover { color: var(--ink-2); }
.tab.active { color: var(--primary-2); border-bottom-color: var(--primary-2); font-weight: 700; }
.tab-count {
  padding: 1px 7px;
  background: var(--bg);
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  color: var(--muted);
}
.tab.active .tab-count { background: var(--primary-soft); color: var(--primary-2); }

/* =========================================================
   KPI CARDS
   ========================================================= */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 14px;
  padding: 20px 0 4px;
}
.kpi-card {
  background: var(--bg-alt);
  border: 1px solid var(--border-soft);
  border-radius: var(--radius-sm);
  padding: 18px 20px;
  transition: all 0.15s;
}
.kpi-card:hover { border-color: var(--border); background: var(--card); box-shadow: var(--shadow-sm); }
.kpi-label { font-size: 12.5px; color: var(--muted); font-weight: 500; margin-bottom: 8px; }
.kpi-value {
  font-size: 28px;
  font-weight: 800;
  color: var(--primary);
  letter-spacing: -0.03em;
  line-height: 1;
  display: flex;
  align-items: baseline;
  gap: 4px;
}
.kpi-value .unit { font-size: 16px; color: var(--primary-2); }
.kpi-trend {
  font-size: 11.5px; font-weight: 600; margin-top: 8px;
  display: inline-flex; align-items: center; gap: 3px;
}
.kpi-trend.up { color: var(--success); }
.kpi-trend.down { color: var(--danger); }
.kpi-trend.neutral { color: var(--muted); }

/* =========================================================
   TABLE
   ========================================================= */
.tbl { width: 100%; border-collapse: collapse; }
.tbl th {
  text-align: left;
  padding: 11px 18px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--muted);
  background: var(--bg-alt);
  border-bottom: 1px solid var(--border);
}
.tbl th:first-child { border-top-left-radius: var(--radius-sm); }
.tbl th:last-child { border-top-right-radius: var(--radius-sm); }
.tbl td {
  padding: 14px 18px;
  font-size: 13.5px;
  color: var(--ink-2);
  border-bottom: 1px solid var(--border-soft);
  vertical-align: middle;
}
.tbl tr:hover td { background: var(--bg-alt); }
.tbl tr:last-child td { border-bottom: none; }
.tbl td.mono { font-family: var(--font-mono); font-size: 12px; color: var(--muted); }
.tbl td .primary-link { color: var(--primary-2); font-weight: 600; cursor: pointer; }
.tbl td .primary-link:hover { text-decoration: underline; }

/* =========================================================
   PILLS
   ========================================================= */
.pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 9px;
  border-radius: 12px;
  font-size: 11.5px;
  font-weight: 600;
}
.pill.success { background: var(--success-soft); color: var(--success); }
.pill.warning { background: var(--warning-soft); color: #b45309; }
.pill.danger  { background: var(--danger-soft);  color: var(--danger); }
.pill.info    { background: var(--info-soft);    color: var(--info); }
.pill.neutral { background: var(--bg);           color: var(--muted); }
.pill.primary { background: var(--primary-soft); color: var(--primary-2); }
.pill.accent  { background: var(--accent-soft);  color: var(--accent-dark); }
.pill .dot { width: 6px; height: 6px; border-radius: 50%; background: currentColor; }

/* =========================================================
   FILTER CHIPS
   ========================================================= */
.filter-chip {
  padding: 7px 14px;
  border: 1px solid var(--border);
  border-radius: 20px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--ink-3);
  background: var(--card);
  cursor: pointer;
  transition: all 0.15s;
}
.filter-chip:hover { border-color: var(--border-strong); color: var(--ink); }
.filter-chip.active { background: var(--ink); color: #fff; border-color: var(--ink); }

/* =========================================================
   PAGE HEADER
   ========================================================= */
.page-title { font-size: 26px; font-weight: 800; color: var(--ink); letter-spacing: -0.02em; line-height: 1.2; }
.page-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}
.page-subtitle { color: var(--muted); font-size: 14px; margin-top: 4px; }
.page-actions { display: flex; gap: 10px; align-items: center; }

/* =========================================================
   MODAL (canonical, token-driven, theme-aware)
   ========================================================= */
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  z-index: 1000;
}
.modal {
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 14px;
  box-shadow: var(--shadow-lg, 0 20px 60px rgba(15, 23, 42, 0.25));
  width: 100%;
  max-width: 540px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.modal.sm { max-width: 420px; }
.modal.lg { max-width: 760px; }
.modal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 22px;
  border-bottom: 1px solid var(--border-soft);
}
.modal-head h2, .modal-head h3, .modal-title {
  font-size: 16px;
  font-weight: 700;
  color: var(--ink);
  letter-spacing: -0.01em;
  margin: 0;
}
.modal-body {
  padding: 22px;
  overflow-y: auto;
}
.modal-foot {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 22px;
  border-top: 1px solid var(--border-soft);
}
.modal-close {
  background: none;
  border: none;
  color: var(--muted);
  cursor: pointer;
  font-size: 20px;
  line-height: 1;
  padding: 4px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.modal-close:hover { background: var(--bg); color: var(--ink); }

/* =========================================================
   DASHBOARD
   ========================================================= */
/* New 2026 dashboard layout — full-width hero + 50/50 rows.
   Replaces the previous `.dash-grid { 1fr 360px }`. Each `.dash-row-2`
   collapses to 1-col at the touch tier so the right card never gets
   crushed below readability. */
.dash-stack {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.dash-row-2 {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 20px;
  align-items: stretch;
}
.dash-row-2 > .card { min-width: 0; }   /* let children (table, chart) shrink */
@media (max-width: 1024px) {
  .dash-row-2 { grid-template-columns: minmax(0, 1fr); }
}

/* Legacy `.dash-grid` kept as a thin alias so any stray markup still renders.
   Remove once all pages are confirmed migrated. */
.dash-grid {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.welcome-banner {
  background: linear-gradient(135deg, #0d1f3c 0%, #1e3a8a 100%);
  color: #fff;
  border-radius: var(--radius);
  padding: 24px 28px;
  margin-bottom: 20px;
  position: relative;
  overflow: hidden;
}
.welcome-banner::before {
  content: '';
  position: absolute;
  top: -60px; right: -60px;
  width: 200px; height: 200px;
  background: radial-gradient(circle, rgba(0,128,201,0.35) 0%, transparent 70%);
  border-radius: 50%;
}
.welcome-banner::after {
  content: '';
  position: absolute;
  bottom: -40px; right: 80px;
  width: 140px; height: 140px;
  background: radial-gradient(circle, rgba(0,128,201,0.25) 0%, transparent 70%);
  border-radius: 50%;
}
.welcome-banner h2 { font-size: 20px; font-weight: 700; margin-bottom: 6px; letter-spacing: -0.02em; position: relative; }
.welcome-banner p { font-size: 14px; color: rgba(255,255,255,0.75); max-width: 560px; position: relative; margin-bottom: 16px; }
.welcome-banner .banner-actions { display: flex; gap: 10px; position: relative; }
.banner-btn { padding: 8px 16px; background: var(--accent); color: #fff; border-radius: 8px; font-weight: 700; font-size: 13px; box-shadow: 0 2px 8px rgba(0,128,201,0.3); display: inline-flex; align-items: center; gap: 6px; }
.banner-btn:hover { background: var(--accent-dark); }
.banner-btn.ghost { background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.15); box-shadow: none; }
.banner-btn.ghost:hover { background: rgba(255,255,255,0.15); }

.recent-courses { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 14px; padding: 20px 22px; }
.course-card { border: 1px solid var(--border); border-radius: var(--radius-sm); overflow: hidden; background: var(--card); transition: all 0.18s; cursor: pointer; display: flex; flex-direction: column; }
.course-card:hover { border-color: var(--accent); box-shadow: var(--shadow-md); transform: translateY(-2px); }
.course-cover { height: 110px; position: relative; overflow: hidden; }
.course-cover .cover-label { position: absolute; top: 12px; left: 12px; background: rgba(255,255,255,0.95); backdrop-filter: blur(4px); padding: 3px 8px; border-radius: 4px; font-size: 10.5px; font-weight: 700; letter-spacing: 0.06em; color: var(--ink); text-transform: uppercase; }
.cover-az   { background: linear-gradient(135deg, #0078d4 0%, #50e6ff 100%); }
.cover-aws  { background: linear-gradient(135deg, #ff9900 0%, #ffcb52 100%); }
.cover-gcp  { background: linear-gradient(135deg, #4285f4 0%, #34a853 100%); }
.cover-comp { background: linear-gradient(135deg, #dc2626 0%, #f97316 100%); }
.cover-cisco{ background: linear-gradient(135deg, #1ba0d7 0%, #6ec5e9 100%); }
.cover-linux{ background: linear-gradient(135deg, #000 0%, #fbbf24 100%); }
.cover-ms365{ background: linear-gradient(135deg, #0078d4 0%, #28a8e0 100%); }
.cover-sc   { background: linear-gradient(135deg, #1e3a8a 0%, #7c3aed 100%); }
.cover-power{ background: linear-gradient(135deg, #742774 0%, #ae158e 100%); }
.cover-gh   { background: linear-gradient(135deg, #161b22 0%, #21262d 100%); }
.cover-k8s  { background: linear-gradient(135deg, #326ce5 0%, #54b0f0 100%); }
.cover-sf   { background: linear-gradient(135deg, #00a1e0 0%, #032d60 100%); }
.cover-sn   { background: linear-gradient(135deg, #62d84e 0%, #003d29 100%); }
.course-logo-wrap {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(255,255,255,0.93);
  border-radius: 7px;
  padding: 5px 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 75%;
  z-index: 1;
  box-shadow: 0 2px 6px rgba(0,0,0,0.10);
}
.course-logo-img {
  max-width: 110px;
  max-height: 38px;
  width: auto;
  height: auto;
  object-fit: contain;
  display: block;
}
.course-body { padding: 14px 16px; flex: 1; display: flex; flex-direction: column; }
.course-title { font-size: 14px; font-weight: 700; color: var(--ink); margin-bottom: 4px; line-height: 1.3; }
.course-sub { font-size: 12px; color: var(--muted); margin-bottom: 12px; }
.course-progress-track { height: 6px; background: var(--bg); border-radius: 3px; overflow: hidden; margin-bottom: 6px; }
.course-progress-fill { height: 100%; background: var(--accent); border-radius: 3px; transition: width 0.4s ease; }
.course-progress-meta { display: flex; justify-content: space-between; font-size: 11.5px; font-weight: 600; color: var(--muted); margin-top: 2px; }
.course-progress-meta .pct { color: var(--accent-dark); }
.course-footer { margin-top: 12px; padding-top: 12px; border-top: 1px solid var(--border-soft); display: flex; justify-content: space-between; align-items: center; font-size: 11.5px; color: var(--muted); }

/* =========================================================
   CATALOG COVERS WITH VENDOR LOGOS
   ========================================================= */
.catalog-cover { height: 140px; position: relative; display: flex; align-items: center; justify-content: center; }
.catalog-cover .meta-row { position: absolute; top: 12px; left: 12px; right: 12px; display: flex; justify-content: space-between; gap: 8px; z-index: 2; }
.catalog-logo-wrap {
  background: rgba(255,255,255,0.93);
  border-radius: 10px;
  padding: 10px 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 80%;
  box-shadow: 0 2px 8px rgba(0,0,0,0.12);
}
.catalog-logo-img {
  max-width: 160px;
  max-height: 56px;
  width: auto;
  height: auto;
  object-fit: contain;
  display: block;
}

/* =========================================================
   CALENDAR
   ========================================================= */
.cal { padding: 20px 22px 24px; width: 100%; }
.cal-header { display: flex; align-items: center; justify-content: space-between; padding: 0 8px 14px; }
.cal-month { font-size: 14.5px; font-weight: 700; color: var(--ink); }
.cal-nav { width: 28px; height: 28px; border-radius: 6px; display: grid; place-items: center; color: var(--muted); cursor: pointer; }
.cal-nav:hover { background: var(--bg); color: var(--ink); }
.cal-grid { display: grid; grid-template-columns: repeat(7, 1fr); column-gap: 2px; row-gap: 0; }
.cal-dow { font-size: 11px; font-weight: 600; color: var(--muted); text-align: center; padding: 8px 0; }
.cal-day { height: 36px; display: grid; place-items: center; font-size: 12px; font-weight: 500; color: var(--ink-2); border-radius: 6px; cursor: pointer; transition: all 0.15s; position: relative; }
.cal-day:hover { background: var(--bg); }
.cal-day.empty { cursor: default; }
.cal-day.empty:hover { background: none; }
.cal-day.today { border: 1.5px solid var(--primary-2); color: var(--primary-2); font-weight: 700; }
.cal-day.has-activity { background: var(--accent-soft); color: var(--accent-dark); font-weight: 700; }
.cal-day.has-activity::after { content: ''; position: absolute; bottom: 3px; left: 50%; transform: translateX(-50%); width: 4px; height: 4px; border-radius: 50%; background: var(--accent); }
.cal-day.selected { background: var(--primary-2); color: #fff; }

/* Calendar view tabs */
.cal-view-tabs { display: flex; gap: 2px; background: var(--bg); padding: 3px; border-radius: 8px; }
.cal-view-btn {
  padding: 5px 10px; font-size: 11.5px; font-weight: 600; color: var(--muted);
  border-radius: 6px; cursor: pointer; transition: all 0.15s;
}
.cal-view-btn:hover { color: var(--ink); }
.cal-view-btn.active { background: var(--card); color: var(--ink); box-shadow: var(--shadow-sm); }

/* Week view */
.cal-week-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 3px; }
.cal-week-col {
  border-radius: 6px; overflow: hidden; cursor: pointer;
  border: 1px solid transparent; transition: border-color 0.15s;
  min-height: 90px; display: flex; flex-direction: column;
}
.cal-week-col:hover { border-color: var(--border); }
.cal-week-col.today { border-color: var(--primary-2); }
.cal-week-col.selected { border-color: var(--accent); }
.cal-week-head { padding: 6px 4px 4px; text-align: center; }
.cal-week-dow { font-size: 9.5px; font-weight: 700; color: var(--muted); text-transform: uppercase; letter-spacing: 0.04em; }
.cal-week-num {
  font-size: 14px; font-weight: 700; color: var(--ink-2); margin-top: 2px;
  width: 26px; height: 26px; border-radius: 50%; display: grid; place-items: center; margin: 2px auto 0;
}
.cal-week-num.today { background: var(--primary-2); color: #fff; }
.cal-week-body { flex: 1; padding: 4px; }
.cal-week-event {
  background: var(--accent-soft); color: var(--accent-dark);
  font-size: 10px; font-weight: 600; padding: 3px 5px; border-radius: 4px;
  display: flex; align-items: center; gap: 4px;
}

/* Agenda view */
.cal-agenda-label {
  font-size: 10px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase;
  color: var(--muted); padding: 0 0 8px;
}
.cal-agenda { display: flex; flex-direction: column; gap: 6px; }
.cal-agenda-row {
  display: flex; align-items: center; gap: 12px;
  padding: 10px 12px; border: 1px solid var(--border-soft);
  border-radius: 8px; background: var(--bg-alt); cursor: pointer;
  transition: all 0.12s;
}
.cal-agenda-row:hover { border-color: var(--border); background: var(--card); box-shadow: var(--shadow-sm); }
.cal-agenda-date {
  text-align: center; min-width: 32px; flex-shrink: 0;
}
.cal-agenda-month { font-size: 9px; font-weight: 700; text-transform: uppercase; color: var(--muted); letter-spacing: 0.06em; }
.cal-agenda-day { font-size: 18px; font-weight: 800; color: var(--ink); line-height: 1; }
.cal-agenda-content { flex: 1; min-width: 0; }
.cal-agenda-title { font-size: 12.5px; font-weight: 600; color: var(--ink); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.cal-agenda-meta { font-size: 11px; color: var(--muted); margin-top: 2px; }

/* =========================================================
   CATALOG
   ========================================================= */
.catalog-filters { display: flex; align-items: center; gap: 12px; margin-bottom: 22px; flex-wrap: wrap; }
.catalog-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 18px; }
.catalog-card { background: var(--card); border: 1px solid var(--border); border-radius: var(--radius); overflow: hidden; transition: all 0.18s; cursor: pointer; display: flex; flex-direction: column; }
.catalog-card:hover { border-color: var(--accent); box-shadow: var(--shadow-md); transform: translateY(-2px); }
.catalog-body { padding: 16px 18px; flex: 1; display: flex; flex-direction: column; }
.catalog-vendor { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: var(--muted); margin-bottom: 4px; }
.catalog-title { font-size: 15px; font-weight: 700; color: var(--ink); line-height: 1.3; margin-bottom: 8px; }
.catalog-stats { display: flex; gap: 12px; font-size: 12px; color: var(--muted); margin-top: auto; padding-top: 12px; border-top: 1px solid var(--border-soft); }
.catalog-stats span { display: inline-flex; align-items: center; gap: 4px; }

/* =========================================================
   RESULTS
   ========================================================= */
.results-hero {
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
  border: 1px solid #a7f3d0;
  border-radius: var(--radius-lg);
  padding: 36px 40px;
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 32px;
  margin-bottom: 24px;
  position: relative;
  overflow: hidden;
}
.results-hero.failed { background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%); border-color: #fecaca; }
.results-verdict-label { font-size: 12px; font-weight: 700; letter-spacing: 0.12em; color: var(--success); text-transform: uppercase; margin-bottom: 6px; }
.results-hero.failed .results-verdict-label { color: var(--danger); }
.results-verdict { font-size: 44px; font-weight: 900; letter-spacing: -0.03em; color: var(--success); line-height: 1; margin-bottom: 10px; }
.results-hero.failed .results-verdict { color: var(--danger); }
.results-exam-name { font-size: 17px; font-weight: 600; color: var(--ink); margin-bottom: 4px; }
.results-exam-meta { color: var(--muted); font-size: 13px; }
.score-ring-wrap { position: relative; width: 160px; height: 160px; }
.score-ring-center { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; }
.score-ring-value { font-size: 42px; font-weight: 800; color: var(--ink); line-height: 1; letter-spacing: -0.03em; }
.score-ring-label { font-size: 10px; font-weight: 700; letter-spacing: 0.08em; color: var(--muted); text-transform: uppercase; margin-top: 4px; }
.competency-row { display: grid; grid-template-columns: 1fr 280px auto; gap: 16px; align-items: center; padding: 14px 0; border-bottom: 1px solid var(--border-soft); }
.competency-row:last-child { border-bottom: none; }
.comp-name { font-weight: 600; color: var(--ink); font-size: 14px; }
.comp-meta { font-size: 11.5px; color: var(--muted); margin-top: 2px; }
.comp-bar-wrap { position: relative; height: 8px; background: var(--bg); border-radius: 4px; overflow: visible; }
.comp-bar-fill { position: absolute; top: 0; left: 0; height: 100%; border-radius: 4px; transition: width 0.5s ease; }
.comp-bar-fill.ok   { background: var(--success); }
.comp-bar-fill.warn { background: var(--warning); }
.comp-bar-fill.alert { background: var(--danger); }
.comp-bar-threshold { position: absolute; top: -3px; bottom: -3px; width: 2px; background: var(--ink); border-radius: 1px; }
.comp-bar-threshold::before { content: '70%'; position: absolute; top: -20px; left: 50%; transform: translateX(-50%); font-size: 9px; color: var(--ink); font-weight: 600; font-family: var(--font-mono); }
.comp-score { font-family: var(--font-mono); font-size: 14px; font-weight: 700; color: var(--ink); min-width: 50px; text-align: right; }

/* =========================================================
   BADGE SHOWCASE
   ========================================================= */
.badge-showcase { background: var(--brand-navy); border-radius: var(--radius-lg); padding: 32px; color: #fff; position: relative; overflow: hidden; }
.badge-showcase::before { content: ''; position: absolute; top: -100px; right: -100px; width: 280px; height: 280px; background: radial-gradient(circle, rgba(0,128,201,0.3) 0%, transparent 65%); border-radius: 50%; }
.badge-issuer { font-size: 11px; font-weight: 700; letter-spacing: 0.15em; text-transform: uppercase; color: rgba(255,255,255,0.5); margin-bottom: 20px; position: relative; }
.badge-icon { width: 84px; height: 84px; background: var(--accent); border-radius: 18px; display: grid; place-items: center; font-size: 40px; font-weight: 800; color: var(--brand-navy); margin-bottom: 24px; box-shadow: 0 8px 32px rgba(0,128,201,0.4); position: relative; }
.badge-name { font-size: 22px; font-weight: 700; letter-spacing: -0.01em; line-height: 1.2; margin-bottom: 8px; position: relative; }
.badge-desc { font-size: 13px; color: rgba(255,255,255,0.7); margin-bottom: 24px; position: relative; line-height: 1.5; }
.badge-meta { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; padding-top: 20px; border-top: 1px solid rgba(255,255,255,0.1); position: relative; }
.badge-meta-label { font-size: 10px; font-weight: 700; letter-spacing: 0.08em; color: rgba(255,255,255,0.5); text-transform: uppercase; margin-bottom: 3px; }
.badge-meta-value { font-family: var(--font-mono); font-size: 12px; font-weight: 500; word-break: break-all; }
.badge-actions { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-top: 16px; }
.badge-actions button { padding: 9px 12px; font-size: 12.5px; border-radius: 6px; font-weight: 700; }
.badge-actions .accept { background: var(--accent); color: #fff; }
.badge-actions .download { background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.15); color: #fff; }

/* =========================================================
   AUTHORING
   ========================================================= */
.auth-layout { display: grid; grid-template-columns: 320px 1fr; gap: 20px; min-height: 680px; }
.auth-bank-panel { background: var(--card); border: 1px solid var(--border); border-radius: var(--radius); display: flex; flex-direction: column; overflow: hidden; }
.auth-bank-head { padding: 16px 18px; border-bottom: 1px solid var(--border); }
.auth-bank-title { font-weight: 700; font-size: 14.5px; margin-bottom: 10px; display: flex; align-items: center; justify-content: space-between; }
.auth-bank-count { font-size: 11.5px; color: var(--muted); font-weight: 600; }
.auth-bank-search { width: 100%; padding: 8px 12px 8px 32px; border: 1px solid var(--border); border-radius: 6px; font-size: 13px; background: var(--bg-alt); }
.auth-bank-search:focus { outline: none; border-color: var(--accent); }
.auth-bank-list { flex: 1; overflow-y: auto; padding: 10px; }
.auth-item-card { border: 1px solid var(--border); border-radius: var(--radius-sm); padding: 12px 14px; margin-bottom: 6px; background: var(--card); cursor: grab; transition: all 0.15s; }
.auth-item-card:hover { border-color: var(--muted-3); box-shadow: var(--shadow-sm); transform: translateX(2px); }
.auth-item-card.in-use { border-color: var(--accent); background: var(--accent-soft); }
.auth-item-meta { display: flex; gap: 8px; align-items: center; margin-bottom: 6px; }
.item-type-tag { font-family: var(--font-mono); font-size: 9.5px; padding: 2px 6px; border-radius: 3px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; }
.item-type-tag.mcq   { background: var(--primary); color: #fff; }
.item-type-tag.match { background: var(--warning); color: #fff; }
.item-type-tag.fill  { background: var(--accent); color: #fff; }
.item-type-tag.order { background: #7c3aed; color: #fff; }
.item-type-tag.essay { background: var(--muted); color: #fff; }
.item-id { font-family: var(--font-mono); font-size: 10.5px; color: var(--muted); }
.auth-item-title { font-size: 12.5px; color: var(--ink); line-height: 1.4; margin-bottom: 6px; }
.auth-item-tags { font-size: 10.5px; color: var(--muted); font-family: var(--font-mono); }
.auth-canvas-panel { background: var(--card); border: 1px solid var(--border); border-radius: var(--radius); display: flex; flex-direction: column; overflow: hidden; }
.auth-canvas-head { padding: 18px 22px; border-bottom: 1px solid var(--border); display: flex; align-items: flex-start; justify-content: space-between; gap: 20px; flex-wrap: wrap; }
.auth-canvas-title h3 { font-size: 20px; font-weight: 800; letter-spacing: -0.02em; color: var(--ink); margin-bottom: 4px; }
.auth-canvas-sub { font-size: 12px; color: var(--muted); font-family: var(--font-mono); display: flex; gap: 10px; flex-wrap: wrap; }
.auth-canvas-sub b { color: var(--ink); font-family: var(--font-sans); }
.state-track { display: flex; align-items: center; padding: 4px; background: var(--bg); border-radius: 8px; }
.state-step { padding: 7px 14px; font-size: 11px; font-weight: 700; color: var(--muted); letter-spacing: 0.08em; text-transform: uppercase; border-radius: 6px; }
.state-step.done { color: var(--ink); }
.state-step.current { background: var(--ink); color: var(--accent); }
.state-step-sep { color: var(--muted-3); font-size: 10px; padding: 0 2px; }
.auth-config-strip { display: grid; grid-template-columns: repeat(4, 1fr); gap: 1px; background: var(--border); border-bottom: 1px solid var(--border); }
.auth-config-cell { padding: 14px 18px; background: var(--bg-alt); }
.auth-config-label { font-size: 10.5px; color: var(--muted); text-transform: uppercase; letter-spacing: 0.08em; font-weight: 700; margin-bottom: 4px; }
.auth-config-value { font-size: 17px; font-weight: 700; color: var(--ink); letter-spacing: -0.01em; }
.auth-config-value .u { font-size: 13px; color: var(--muted); font-weight: 600; margin-left: 2px; }
.auth-canvas-body { padding: 18px 22px; flex: 1; overflow-y: auto; }
.composition-label { font-size: 13.5px; font-weight: 700; color: var(--ink); margin-bottom: 12px; display: flex; justify-content: space-between; align-items: center; }
.composition-zone { background: var(--bg-alt); border: 1px dashed var(--border-strong); border-radius: var(--radius-sm); padding: 8px; }
.comp-row-item { background: var(--card); border: 1px solid var(--border); border-radius: var(--radius-sm); padding: 12px 16px; margin-bottom: 6px; display: grid; grid-template-columns: 28px auto 1fr auto auto; gap: 14px; align-items: center; transition: all 0.15s; }
.comp-row-item:hover { box-shadow: var(--shadow-sm); border-color: var(--muted-3); }
.comp-row-item.highlighted { border-color: var(--accent); background: var(--accent-soft); }
.comp-row-num { font-family: var(--font-mono); font-weight: 700; color: var(--muted); font-size: 13px; }
.comp-row-text { font-size: 13px; color: var(--ink); }
.comp-row-weight { font-family: var(--font-mono); font-size: 11.5px; color: var(--muted); }
.comp-row-grip { color: var(--muted-3); cursor: grab; padding: 4px; }
.drop-hint { text-align: center; padding: 20px; font-size: 12px; color: var(--muted); font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase; border: 1px dashed var(--border); border-radius: var(--radius-sm); margin-top: 8px; background: var(--card); }

/* =========================================================
   KEYS
   ========================================================= */
.keys-summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-bottom: 22px; }

/* =========================================================
   SETTINGS
   ========================================================= */
.settings-grid { display: grid; grid-template-columns: 220px 1fr; gap: 32px; }
.settings-nav { display: flex; flex-direction: column; gap: 2px; }
.settings-nav a { padding: 9px 14px; border-radius: 6px; font-size: 13.5px; color: var(--ink-3); font-weight: 500; cursor: pointer; display: block; }
.settings-nav a:hover { background: var(--bg); color: var(--ink); }
.settings-nav a.active { background: var(--primary-soft); color: var(--primary-2); font-weight: 700; }
.form-row { display: grid; grid-template-columns: 200px 1fr; gap: 24px; padding: 16px 0; border-bottom: 1px solid var(--border-soft); align-items: center; }
.form-label { font-weight: 600; font-size: 13.5px; color: var(--ink); }
.form-label .hint { display: block; color: var(--muted); font-weight: 400; font-size: 12px; margin-top: 2px; }
.form-input { width: 100%; max-width: 420px; padding: 8px 12px; border: 1px solid var(--border); border-radius: var(--radius-sm); font-size: 13.5px; }
.form-input:focus { outline: none; border-color: var(--primary-2); box-shadow: 0 0 0 3px rgba(0,128,201,0.1); }
.switch { width: 40px; height: 22px; background: var(--muted-3); border-radius: 11px; position: relative; cursor: pointer; transition: background 0.2s; }
.switch::after { content: ''; position: absolute; left: 2px; top: 2px; width: 18px; height: 18px; background: #fff; border-radius: 50%; transition: all 0.2s; box-shadow: 0 1px 3px rgba(0,0,0,0.2); }
.switch.on { background: var(--accent); }
.switch.on::after { left: 20px; }

/* =========================================================
   COMMAND PALETTE
   ========================================================= */
.palette-overlay {
  position: fixed; inset: 0;
  background: rgba(15,23,42,0.45);
  backdrop-filter: blur(2px);
  z-index: 200;
  display: none;
  align-items: flex-start;
  justify-content: center;
  padding-top: 120px;
  animation: paletteIn 0.15s ease;
}
.palette-overlay.open { display: flex; }
@keyframes paletteIn { from { opacity: 0; } to { opacity: 1; } }
.palette { width: 600px; max-width: calc(100% - 32px); background: var(--card); border-radius: 12px; box-shadow: var(--shadow-pop); overflow: hidden; animation: paletteScale 0.15s ease; }
@keyframes paletteScale { from { transform: translateY(-8px) scale(0.98); opacity: 0; } to { transform: translateY(0) scale(1); opacity: 1; } }
.palette-input { width: 100%; padding: 18px 20px 18px 52px; border: none; border-bottom: 1px solid var(--border); font-size: 15px; background: var(--card); }
.palette-input:focus { outline: none; }
.palette-results { max-height: 400px; overflow-y: auto; padding: 8px; }
.palette-section-label { font-size: 10.5px; font-weight: 700; letter-spacing: 0.08em; color: var(--muted); text-transform: uppercase; padding: 10px 12px 6px; }
.palette-item { display: flex; align-items: center; gap: 12px; padding: 10px 12px; border-radius: 6px; cursor: pointer; font-size: 13.5px; }
.palette-item:hover, .palette-item.active { background: var(--bg); }
.palette-item-title { font-weight: 600; color: var(--ink); }
.palette-item-sub { color: var(--muted); font-size: 12px; margin-top: 1px; }
.palette-item-kbd { margin-left: auto; font-family: var(--font-mono); font-size: 11px; color: var(--muted-2); padding: 2px 6px; border: 1px solid var(--border); border-radius: 4px; }

/* =========================================================
   TOAST
   ========================================================= */
.toast {
  position: fixed;
  bottom: 24px; left: 50%;
  transform: translateX(-50%) translateY(20px);
  background: var(--ink);
  color: #fff;
  padding: 10px 18px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  box-shadow: var(--shadow-lg);
  opacity: 0;
  pointer-events: none;
  transition: all 0.25s ease;
  z-index: 300;
  display: flex;
  align-items: center;
  gap: 10px;
}
.toast.show { opacity: 1; transform: translateX(-50%) translateY(0); }
.toast .tdot { width: 8px; height: 8px; border-radius: 50%; background: var(--accent); }

/* =========================================================
   EMPTY STATE
   ========================================================= */
.empty { padding: 48px 24px; text-align: center; color: var(--muted); }
.empty-icon { width: 52px; height: 52px; background: var(--bg); border-radius: 14px; display: grid; place-items: center; margin: 0 auto 14px; color: var(--muted-2); }
.empty-title { font-size: 15px; font-weight: 700; color: var(--ink); margin-bottom: 4px; }
.empty-text { font-size: 13px; max-width: 340px; margin: 0 auto 16px; }

/* =========================================================
   TEST RUNNER
   ========================================================= */
.test-runner { position: fixed; inset: 0; background: var(--bg); z-index: 100; display: grid; grid-template-rows: 56px 1fr; }
.test-topbar { background: var(--brand-navy); color: #fff; display: flex; align-items: center; padding: 0 24px; gap: 24px; }
.test-exam-title { font-weight: 700; font-size: 14px; flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.test-topbar-meta { display: flex; align-items: center; gap: 16px; font-size: 12.5px; color: var(--brand-navy-muted); }
.test-timer { display: flex; align-items: center; gap: 8px; padding: 5px 14px; background: rgba(0,128,201,0.15); border: 1px solid rgba(0,128,201,0.3); border-radius: 6px; color: var(--accent); font-family: var(--font-mono); font-weight: 600; font-variant-numeric: tabular-nums; }
.test-save-indicator { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; color: var(--brand-navy-muted); }
.test-save-indicator::before { content: ''; width: 6px; height: 6px; border-radius: 50%; background: var(--accent); animation: testPulse 2s ease-in-out infinite; }
@keyframes testPulse { 0%,100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.4; transform: scale(0.8); } }
.test-close { padding: 6px 12px; background: rgba(255,255,255,0.08); border-radius: 6px; font-size: 12.5px; font-weight: 600; color: #fff; }
.test-close:hover { background: rgba(255,255,255,0.15); }
.test-body { display: grid; grid-template-columns: 1fr 280px; overflow: hidden; }
.test-main { overflow-y: auto; padding: 32px 48px 48px; }
.test-container { max-width: 780px; margin: 0 auto; }
.test-q-head { display: flex; justify-content: space-between; align-items: center; padding-bottom: 14px; margin-bottom: 24px; border-bottom: 1px solid var(--border); }
.test-q-num { font-size: 13px; font-weight: 700; color: var(--muted); letter-spacing: 0.04em; text-transform: uppercase; }
.test-q-chips { display: flex; gap: 8px; }
.test-q-prompt { font-size: 17px; font-weight: 600; color: var(--ink); line-height: 1.55; margin-bottom: 24px; }
.test-q-context { background: var(--info-soft); border-left: 3px solid var(--info); padding: 14px 18px; border-radius: 0 6px 6px 0; font-size: 13.5px; color: var(--ink-3); margin-bottom: 24px; line-height: 1.55; }
.test-answers { display: flex; flex-direction: column; gap: 10px; margin-bottom: 36px; }
.test-answer { display: grid; grid-template-columns: 28px 1fr auto; gap: 14px; padding: 14px 18px; border: 1.5px solid var(--border); border-radius: var(--radius-sm); background: var(--card); cursor: pointer; transition: all 0.15s; align-items: center; }
.test-answer:hover { border-color: var(--muted-3); background: var(--bg-alt); }
.test-answer.selected { border-color: var(--accent); background: var(--accent-soft); }
.test-answer-letter { width: 26px; height: 26px; border-radius: 6px; border: 1.5px solid var(--border); display: grid; place-items: center; font-size: 12.5px; font-weight: 700; color: var(--muted); background: var(--card); font-family: var(--font-mono); }
.test-answer.selected .test-answer-letter { background: var(--accent); border-color: var(--accent); color: #fff; }
.test-answer-text { font-size: 14.5px; color: var(--ink); line-height: 1.45; }
.test-answer-kbd { font-family: var(--font-mono); font-size: 11px; color: var(--muted-2); padding: 2px 6px; border: 1px solid var(--border); border-radius: 4px; background: var(--bg); }
.test-actions { display: flex; justify-content: space-between; padding-top: 24px; border-top: 1px solid var(--border); }
.test-nav-panel { background: var(--card); border-left: 1px solid var(--border); padding: 24px 22px; overflow-y: auto; }
.test-nav-title { font-size: 11px; font-weight: 700; color: var(--muted); letter-spacing: 0.1em; text-transform: uppercase; margin-bottom: 14px; }
.test-nav-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 6px; margin-bottom: 20px; }
.test-nav-cell { aspect-ratio: 1; display: grid; place-items: center; font-family: var(--font-mono); font-size: 12px; font-weight: 600; border: 1px solid var(--border); background: var(--card); color: var(--ink-3); border-radius: 5px; cursor: pointer; position: relative; }
.test-nav-cell:hover { border-color: var(--muted-3); }
.test-nav-cell.answered { background: var(--ink); color: #fff; border-color: var(--ink); }
.test-nav-cell.current { background: var(--accent); color: #fff; border-color: var(--accent); }
.test-nav-cell.flagged::after { content: ''; position: absolute; top: -2px; right: -2px; width: 8px; height: 8px; background: var(--warning); border: 1px solid var(--card); border-radius: 50%; }
.test-nav-legend { display: grid; gap: 8px; font-size: 11.5px; color: var(--muted); padding-top: 16px; border-top: 1px solid var(--border); margin-bottom: 20px; }
.test-nav-legend-item { display: flex; align-items: center; gap: 8px; }
.test-nav-legend-item .swatch { width: 12px; height: 12px; border-radius: 3px; }
.test-summary { background: var(--bg-alt); border-radius: 8px; padding: 14px; margin-bottom: 16px; }
.test-summary-row { display: flex; justify-content: space-between; font-size: 12.5px; padding: 4px 0; }
.test-summary-row .label { color: var(--muted); }
.test-summary-row .value { font-weight: 700; color: var(--ink); font-family: var(--font-mono); }

/* =========================================================
   RESPONSIVE  (mobile-first hardening on top of desktop)
   =========================================================
   Two layout tiers — clean and predictable:
     drawer    ≤ 1024 px   hamburger + off-canvas sidebar
                            (covers all touch devices: phone + tablet)
     desktop   > 1024 px    full inline sidebar; user can opt-in to
                            icon-rail via the collapse button

   Why no "icon-rail tier" between phone and desktop? Tablets are touch
   devices. The desktop icon-rail relies on hover-flyouts to reveal
   submenus, which is unreliable on touch (iPad/Android tablets). The
   drawer pattern works on every input modality.

   The drawer is a self-contained fixed element with its own stacking
   context, so it doesn't inherit ordering quirks from the desktop
   `.app.collapsed` chain. iOS-specific hardening (safe-area, dvh,
   no overflow:hidden on body) lives here and in styles/responsive.css.
*/

/* ── Hamburger button (topbar) ─────────────────────────────────── */
.topbar-hamburger {
  display: none;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  align-items: center;
  justify-content: center;
  color: var(--ink);
  background: var(--bg-alt);
  border: 1px solid var(--border);
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s, border-color 0.15s, color 0.15s, transform 0.1s;
}
.topbar-hamburger:hover  { border-color: var(--accent); color: var(--accent); }
.topbar-hamburger:active { transform: scale(0.94); }
.topbar-hamburger:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }

/* ── Sidebar close button (drawer only) ────────────────────────── */
.sidebar-close {
  display: none;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.85);
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.10);
  cursor: pointer;
  margin-left: auto;
  flex-shrink: 0;
  transition: background 0.15s, color 0.15s, transform 0.1s;
}
.sidebar-close:hover  { background: rgba(255, 255, 255, 0.12); color: #fff; }
.sidebar-close:active { transform: scale(0.94); }
.sidebar-close:focus-visible { outline: 2px solid var(--accent); outline-offset: 1px; }

/* ── Backdrop ──────────────────────────────────────────────────── */
.sidebar-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);
  z-index: 95;
  animation: backdropFadeIn 0.22s ease;
}
@keyframes backdropFadeIn {
  from { opacity: 0; }
  to   { opacity: 1; }
}

/* ── DRAWER TIER (≤ 1024 px) ─────────────────────────────────────
   Phone + tablet share this exact layout. Hamburger appears; sidebar
   becomes an off-canvas drawer. Every desktop rule that would otherwise
   apply (icon-only sidebar, hover-flyout submenus, sticky positioning)
   is force-reset. Using `!important` is intentional — the drawer is a
   categorically different layout context, not a tweak of desktop. */
@media (max-width: 1024px) {

  /* ── Grid collapse ──────────────────────────────────────────── */
  .app,
  .app.collapsed { grid-template-columns: 1fr !important; }

  /* Hamburger appears in the topbar. */
  .topbar-hamburger { display: inline-flex; }

  /* ── Drawer container ───────────────────────────────────────── */
  .sidebar,
  .app.collapsed .sidebar {
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    bottom: 0 !important;
    height: 100vh !important;
    height: 100dvh !important;            /* iOS toolbar-aware */
    /* Tablet: a touch wider so labels breathe; phone clamps via ≤ 768 px. */
    width: clamp(280px, 40vw, 360px) !important;
    z-index: 100 !important;
    transform: translate3d(-100%, 0, 0) !important;
    transition: transform 0.28s cubic-bezier(0.32, 0.72, 0.0, 1) !important;
    will-change: transform;
    box-shadow:
      0 30px 80px rgba(0, 0, 0, 0.55),
      0 4px 16px rgba(0, 0, 0, 0.35);
    overflow: hidden;
    isolation: isolate;
    padding-top:    env(safe-area-inset-top);
    padding-bottom: env(safe-area-inset-bottom);
  }
  /* Keep the header/footer pinned inside the drawer too; only the middle
     nav region scrolls (touch-friendly). */
  .sidebar .sidebar-scroll {
    overflow-y: auto;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
  }
  .app.mobile-open .sidebar,
  .app.mobile-open.collapsed .sidebar {
    transform: translate3d(0, 0, 0) !important;
  }

  /* ── Drawer header (logo + × close) ─────────────────────────── */
  .sidebar .sidebar-header {
    justify-content: space-between !important;
    padding: 0 14px !important;
    height: calc(var(--topbar-h) + 4px);
    flex-shrink: 0;
  }
  .sidebar .sidebar-close { display: inline-flex; }
  .sidebar .logo-img      { display: block !important; max-width: 150px; }
  .sidebar .logo-icon,
  .app.collapsed .sidebar .logo-icon { display: none !important; }

  /* ── Force expanded labels (defeat `.app.collapsed .X` rules) ── */
  .sidebar .logo-text,
  .app.collapsed .sidebar .logo-text,
  .sidebar .nav-label,
  .app.collapsed .sidebar .nav-label,
  .sidebar .nav-chevron,
  .app.collapsed .sidebar .nav-chevron,
  .sidebar .sidebar-section-label,
  .app.collapsed .sidebar .sidebar-section-label,
  .sidebar .collapse-btn-label,
  .app.collapsed .sidebar .collapse-btn-label,
  .sidebar .sidebar-footer-btn-label,
  .app.collapsed .sidebar .sidebar-footer-btn-label {
    display: inline !important;
  }

  /* ── Touch-friendly nav items ───────────────────────────────── */
  .sidebar .nav-item,
  .app.collapsed .sidebar .nav-item {
    justify-content: flex-start !important;
    padding: 12px 16px !important;
    font-size: 14px;
    border-radius: 8px;
    min-height: 44px;
  }
  .sidebar .nav-subitem,
  .app.collapsed .sidebar .nav-subitem {
    padding: 11px 14px !important;
    font-size: 13.5px;
    min-height: 40px;
    line-height: 1.3;
  }
  .sidebar .nav-subitem-wrap {
    margin-bottom: 2px;
  }

  /* ── Submenus: accordion, NOT hover-flyout ──────────────────── */
  .sidebar .nav-subitems,
  .app.collapsed .sidebar .nav-subitems {
    position: static !important;
    display: block !important;
    left: auto !important;
    top: auto !important;
    min-width: 0 !important;
    background: transparent !important;
    border: 0 !important;
    box-shadow: none !important;
    padding: 0 0 0 26px !important;
    max-height: 0 !important;
    overflow: hidden !important;
    animation: none !important;
    transition: max-height 0.28s cubic-bezier(0.4, 0, 0.2, 1) !important;
  }
  .sidebar .nav-subitems.open,
  .app.collapsed .sidebar .nav-subitems.open {
    max-height: 800px !important;
    padding-bottom: 4px !important;
  }
  /* Cancel the desktop hover-flyout (and its invisible bridge). */
  .sidebar .nav-section:hover > .nav-subitems,
  .app.collapsed .sidebar .nav-section:hover > .nav-subitems {
    position: static !important;
    box-shadow: none !important;
    background: transparent !important;
    border: 0 !important;
    padding: 0 0 0 26px !important;
    animation: none !important;
  }
  .sidebar .nav-section:hover > .nav-subitems::before,
  .app.collapsed .sidebar .nav-section:hover > .nav-subitems::before {
    content: none !important;
  }
  .sidebar .nav-flyout-title,
  .app.collapsed .sidebar .nav-flyout-title {
    display: none !important;
  }

  /* ── Cascade degrades to a nested/indented accordion on the drawer ──
     The level-2 subpanel is no longer a fixed cascade; it renders inline,
     always visible, indented under its group heading. */
  .sidebar .nav-flyout-group,
  .app.collapsed .sidebar .nav-flyout-group {
    position: static !important;
  }
  .sidebar .nav-flyout-group-row,
  .app.collapsed .sidebar .nav-flyout-group-row {
    cursor: default !important;
    background: transparent !important;
    padding-left: 0 !important;
    font-size: 10px !important;
    font-weight: 700 !important;
    letter-spacing: 0.05em !important;
    text-transform: uppercase !important;
    opacity: 0.85 !important;
  }
  .sidebar .nav-flyout-group-chevron,
  .app.collapsed .sidebar .nav-flyout-group-chevron {
    display: none !important;
  }
  .sidebar .nav-flyout-group .nav-subpanel,
  .app.collapsed .sidebar .nav-flyout-group .nav-subpanel,
  .sidebar .nav-flyout-group.has-children:hover > .nav-subpanel,
  .app.collapsed .sidebar .nav-flyout-group.has-children:hover > .nav-subpanel {
    display: block !important;
    position: static !important;
    left: auto !important;
    top: auto !important;
    min-width: 0 !important;
    max-height: none !important;
    overflow: visible !important;
    margin-left: 0 !important;
    padding: 0 0 0 12px !important;
    background: transparent !important;
    border: 0 !important;
    box-shadow: none !important;
    animation: none !important;
    z-index: auto !important;
  }
  .sidebar .nav-flyout-group.has-children:hover > .nav-subpanel::before,
  .app.collapsed .sidebar .nav-flyout-group.has-children:hover > .nav-subpanel::before,
  .sidebar .nav-flyout-group.has-children:hover::after,
  .app.collapsed .sidebar .nav-flyout-group.has-children:hover::after,
  .sidebar .nav-flyout-group.has-children.is-open::after,
  .app.collapsed .sidebar .nav-flyout-group.has-children.is-open::after {
    content: none !important;
  }
  .sidebar .fav-toggle         { opacity: 0.6 !important; }
  .sidebar .fav-toggle.active  { opacity: 1   !important; }

  /* Drawer has no use for the desktop collapse toggle. */
  .sidebar .collapse-btn { display: none !important; }

  /* ── Main area / topbar ─────────────────────────────────────── */
  .main-area { width: 100%; min-width: 0; }

  .topbar {
    padding-left:  max(16px, env(safe-area-inset-left));
    padding-right: max(16px, env(safe-area-inset-right));
  }
}

/* ── Tablet refinements (between phone and desktop) ───────────────
   Tablet keeps search and breadcrumbs visible; phone (next block) hides
   them. This gives iPad portrait a usable, professional look. */
@media (min-width: 769px) and (max-width: 1024px) {
  .topbar         { gap: 16px; }
  .topbar-search  { max-width: 320px; }
  .topbar-actions { gap: 12px; }
  .breadcrumbs    { font-size: 12.5px; }
  .content        { padding: 22px 24px; }

  /* Per-view grids: collapse at this tier where they would otherwise
     overflow. Keys' 4-up KPI grid drops to 2-up here (phone tier collapses
     it further to 2 / 1 already). */
  .dash-grid      { grid-template-columns: 1fr !important; }
  .keys-summary   { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }

  /* Result-detail competency row: 280 px middle column starves the
     name/score on iPad portrait. Stack the row at this tier so the
     bar gets full width. */
  .competency-row {
    grid-template-columns: 1fr !important;
    gap: 8px;
  }
}

/* ── Phone (≤ 768 px): drop search, breadcrumbs, tighten chrome ── */
@media (max-width: 768px) {
  .topbar {
    gap: 10px;
    padding-left:  max(12px, env(safe-area-inset-left));
    padding-right: max(12px, env(safe-area-inset-right));
    flex-wrap: nowrap;
  }
  .topbar-search  { display: none; }
  .breadcrumbs    { display: none; }
  .topbar-actions { margin-left: auto; gap: 6px; }
  .topbar-group   { gap: 6px; }

  /* Org switcher → badge-only on phones to save space. */
  .org-switch-btn { max-width: none; padding: 5px; }
  .org-switch-text,
  .org-switch-btn > svg { display: none; }
  .org-switch-dropdown { min-width: 220px; }

  /* Drawer slightly tighter on phone — match thumb reach. */
  .sidebar,
  .app.collapsed .sidebar {
    width: min(88vw, 320px) !important;
  }

  /* Page chrome */
  .content {
    padding: 16px 14px max(16px, env(safe-area-inset-bottom));
  }

  /* ── Per-view grid collapses ─────────────────────────────────── */
  .dash-grid          { grid-template-columns: 1fr !important; }
  .auth-layout        { grid-template-columns: 1fr !important; }
  .settings-grid      { grid-template-columns: 1fr !important; }
  .test-body          { grid-template-columns: 1fr !important; }
  .test-nav-panel     { display: none; }
  .test-main          { padding: 18px; }
  .auth-config-strip  { grid-template-columns: 1fr !important; }
  .keys-summary       { grid-template-columns: 1fr 1fr !important; }
  .results-hero       { grid-template-columns: 1fr !important; text-align: center; }
  .score-ring-wrap    { margin: 0 auto; }
  .competency-row     { grid-template-columns: 1fr !important; gap: 8px; }

  /* Calendar grids stay 7-col but cells shrink. */
  .cal-grid, .cal-week-grid { font-size: 11px; }

  .reorder-overlay  { padding: 12px; }
  .reorder-dialog   { max-width: 100%; }
}

/* ── Phone-portrait / very narrow ─────────────────────────────── */
@media (max-width: 480px) {
  .topbar          { padding: 0 10px; gap: 6px; }
  .topbar-icon     { width: 38px; height: 38px; }
  .keys-summary    { grid-template-columns: 1fr !important; }
  .content         { padding: 14px 12px; }
  .sidebar,
  .app.collapsed .sidebar { width: 92vw !important; }
}

/* ── Touch-only: kill desktop hover-flyout under all conditions ─ */
@media (hover: none) {
  .nav-section:hover > .nav-subitems,
  .app.collapsed .nav-section:hover > .nav-subitems {
    display: none !important;
  }
  .nav-section:hover > .nav-subitems::before,
  .app.collapsed .nav-section:hover > .nav-subitems::before {
    content: none !important;
  }
}

/* Kill the cascade level-2 hover on wide touch screens (where the drawer
   accordion does NOT apply) so it can't get stuck open. Scoped to
   >1024px so the ≤1024px drawer accordion keeps its inline subpanel. */
@media (hover: none) and (min-width: 1025px) {
  .nav-flyout-group.has-children:hover > .nav-subpanel,
  .nav-flyout-group.has-children.is-open > .nav-subpanel {
    display: none !important;
  }
  .nav-flyout-group.has-children:hover > .nav-subpanel::before,
  .nav-flyout-group.has-children.is-open > .nav-subpanel::before,
  .nav-flyout-group.has-children:hover::after,
  .nav-flyout-group.has-children.is-open::after {
    content: none !important;
  }
}

/* =========================================================
   REORDER ENTERPRISE SECTIONS — MODAL
   ========================================================= */
.reorder-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.48);
  backdrop-filter: blur(2px);
  display: grid;
  place-items: center;
  z-index: 200;
  padding: 20px;
}
.reorder-dialog {
  width: 100%;
  max-width: 420px;
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 14px;
  box-shadow: var(--shadow-pop, 0 24px 60px rgba(15,23,42,0.28));
  overflow: hidden;
  animation: reorderIn 0.18s cubic-bezier(0.4,0,0.2,1);
}
@keyframes reorderIn {
  from { opacity: 0; transform: translateY(12px) scale(0.98); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}
.reorder-fade-enter-active, .reorder-fade-leave-active { transition: opacity 0.15s ease; }
.reorder-fade-enter, .reorder-fade-leave-to { opacity: 0; }

.reorder-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 20px 12px;
}
.reorder-title {
  font-size: 16px;
  font-weight: 700;
  color: var(--ink);
  letter-spacing: -0.01em;
}
.reorder-subtitle {
  font-size: 12.5px;
  color: var(--muted);
  margin-top: 4px;
  line-height: 1.4;
}
.reorder-close {
  width: 28px; height: 28px;
  display: grid; place-items: center;
  border-radius: 6px;
  color: var(--muted);
  background: none;
  border: none;
  cursor: pointer;
  transition: all 0.13s;
  flex-shrink: 0;
}
.reorder-close:hover { color: var(--ink); background: var(--bg); }

.reorder-list {
  list-style: none;
  margin: 0;
  padding: 4px 12px 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.reorder-group + .reorder-group { border-top: 1px solid var(--border-soft, var(--border)); }
.reorder-group-label {
  padding: 12px 16px 2px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--muted);
}
.reorder-row {
  display: grid;
  grid-template-columns: 20px 28px 1fr auto auto;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--card);
  cursor: grab;
  transition: background 0.13s, border-color 0.13s, box-shadow 0.13s, transform 0.13s;
  user-select: none;
}
.reorder-row:hover { background: var(--bg-alt); border-color: var(--border-strong, var(--border)); }
.reorder-row:active { cursor: grabbing; }
.reorder-row.dragging {
  opacity: 0.5;
  border-style: dashed;
  border-color: var(--accent);
  background: var(--accent-soft);
}
.reorder-handle { color: var(--muted-3); display: grid; place-items: center; }
.reorder-icon {
  width: 28px; height: 28px;
  display: grid; place-items: center;
  border-radius: 7px;
  background: var(--accent-soft);
  color: var(--accent-dark);
}
.reorder-name {
  font-size: 13.5px;
  font-weight: 600;
  color: var(--ink);
  min-width: 0;
}
.reorder-position {
  font-size: 11px;
  font-weight: 700;
  color: var(--muted);
  font-variant-numeric: tabular-nums;
  padding: 2px 7px;
  border-radius: 9999px;
  background: var(--bg);
}
.reorder-move {
  display: flex;
  gap: 2px;
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: 7px;
  padding: 2px;
}
.reorder-move-btn {
  width: 24px; height: 22px;
  display: grid; place-items: center;
  border-radius: 4px;
  background: none;
  border: none;
  color: var(--muted);
  cursor: pointer;
  transition: all 0.13s;
}
.reorder-move-btn:hover:not(:disabled) { color: var(--ink); background: var(--card); }
.reorder-move-btn:focus-visible { outline: 2px solid var(--accent); outline-offset: 1px; }
.reorder-move-btn:disabled { opacity: 0.3; cursor: not-allowed; }

.reorder-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 20px 20px;
  border-top: 1px solid var(--border-soft);
  margin-top: 4px;
}
.reorder-reset {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  color: var(--muted);
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px 0;
  font-weight: 500;
  transition: color 0.13s;
}
.reorder-reset:hover { color: var(--ink); }
.reorder-footer-actions { display: flex; gap: 8px; }

/* Collapsed-sidebar touch-up: in compact mode the footer button only shows the icon, centered */
.app.collapsed .sidebar-footer { padding: 10px 8px; align-items: center; }
</style>
